/* GHALIA — global UI behaviours.
   Vanilla JS. Custom elements for drawers, dialog, qty, header scroll, accordion, marquee duplication.
   No external libraries. */

(() => {
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  };

  /* ---------- Sticky header scroll state + pinned nav ---------- */
  const header = document.querySelector('[data-site-header]');
  const primaryBar = document.querySelector('[data-announcement-primary]');
  const utilityBar = document.querySelector('[data-announcement-utility]');

  const getPrimaryScrollOffset = () => {
    if (!primaryBar || primaryBar.classList.contains('is-hidden')) return 0;
    return Math.max(0, primaryBar.offsetHeight - window.scrollY);
  };

  const isHeaderStackPinned = () => window.scrollY > 4;

  const setSpacer = (el, active, height) => {
    if (!el) return;
    el.classList.toggle('is-active', active);
    el.style.height = active ? `${height}px` : '0';
  };

  const pinUtilityBar = () => {
    const utilitySpacer = document.querySelector('[data-ann-utility-spacer]');
    const headerSpacer = document.querySelector('[data-header-nav-spacer]');
    const utilityH = utilityBar && !utilityBar.classList.contains('is-hidden') ? utilityBar.offsetHeight : 0;
    const headerH = header?.offsetHeight || 0;
    const pinned = isHeaderStackPinned();

    primaryBar?.classList.toggle('is-scrolled-away', pinned && !primaryBar.classList.contains('is-hidden'));

    if (utilityBar) {
      utilityBar.classList.toggle('is-pinned', pinned);
      setSpacer(utilitySpacer, pinned, utilityH);
    }

    if (header) {
      header.classList.toggle('is-nav-pinned', pinned);
      setSpacer(headerSpacer, pinned, headerH);
    }
  };

  const pinHeaderOnly = () => {
    const headerSpacer = document.querySelector('[data-header-nav-spacer]');
    const headerH = header?.offsetHeight || 0;
    const pinned = isHeaderStackPinned();

    primaryBar?.classList.toggle('is-scrolled-away', pinned && !primaryBar?.classList.contains('is-hidden'));

    if (header) {
      header.classList.toggle('is-nav-pinned', pinned);
      setSpacer(headerSpacer, pinned, headerH);
    }
    document.documentElement.style.setProperty('--sticky-nav-top', '0px');
  };

  const isMobileNavViewport = () => window.matchMedia('(max-width: 1024px)').matches;

  const updateMobileNavInset = () => {
    if (!isMobileNavViewport()) return;
    const group = document.querySelector('.shopify-section-group-header-group');
    const navHeader = document.querySelector('[data-site-header]');
    if (!group && !navHeader) return;
    const groupBottom = group?.getBoundingClientRect().bottom ?? 0;
    const headerBottom = navHeader?.getBoundingClientRect().bottom ?? 0;
    const inset = Math.max(0, Math.round(Math.max(groupBottom, headerBottom)));
    document.documentElement.style.setProperty('--mobile-nav-top', `${inset}px`);

    syncMobileNavPad();
  };

  const syncMobileNavPad = () => {
    if (!isMobileNavViewport()) return;
    const header = document.querySelector('[data-site-header]');
    const mark =
      header?.querySelector('.site-header__logo-img') ||
      header?.querySelector('.site-header__logo');
    const toggle = header?.querySelector('[data-mobile-nav-toggle]');
    const drawer = document.querySelector('[data-mobile-nav]');
    if (!mark) return;

    const markRect = mark.getBoundingClientRect();
    const rtl = document.documentElement.dir === 'rtl';
    const drawerRect = drawer?.getBoundingClientRect();
    const drawerWidth = drawerRect?.width || window.innerWidth * 0.85;
    const maxAlign = Math.max(16, Math.min(96, Math.round(drawerWidth * 0.42)));

    let align = rtl
      ? Math.max(0, Math.round(window.innerWidth - markRect.right))
      : Math.max(0, Math.round(markRect.left));

    if (drawer?.dataset.open === 'true' && drawerRect && drawerRect.width > 0) {
      const drawerLeft = drawerRect.left;
      const drawerRelative =
        rtl
          ? Math.max(0, Math.round(drawerRect.right - markRect.right))
          : Math.max(0, Math.round(markRect.left - drawerLeft));
      if (drawerLeft >= -4 && drawerLeft < window.innerWidth * 0.25) {
        align = drawerRelative;
      }
    }

    align = Math.min(align, maxAlign);
    document.documentElement.style.setProperty('--mobile-nav-align', `${align}px`);

    const toggleRect = toggle?.getBoundingClientRect();
    const headerPad = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-pad-inline') || '12',
      10
    );
    const footPadMax = Math.max(headerPad + 8, 56);
    let backPad = rtl
      ? Math.max(0, Math.round(window.innerWidth - (toggleRect?.right ?? markRect.left)))
      : Math.max(0, Math.round(toggleRect?.left ?? markRect.left));

    if (drawer?.dataset.open === 'true' && drawerRect && drawerRect.width > 0 && toggleRect) {
      const drawerLeft = drawerRect.left;
      if (drawerLeft >= -4 && drawerLeft < window.innerWidth * 0.25) {
        backPad = rtl
          ? Math.max(0, Math.round(drawerRect.right - toggleRect.right))
          : Math.max(0, Math.round(toggleRect.left - drawerLeft));
      }
    }

    backPad = Math.min(backPad, footPadMax);
    document.documentElement.style.setProperty('--mobile-nav-back-pad', `${backPad}px`);

    /* Footer (Farah + account) — align with header X (toggle inset + small nudge) */
    const footPad = Math.min(Math.max(backPad + 8, headerPad + 8), footPadMax);
    document.documentElement.style.setProperty('--mobile-nav-foot-pad', `${footPad}px`);

    const panelTitle = drawer?.querySelector('[data-mobile-nav-panel-title]');
    const panelBar = drawer?.querySelector('[data-mobile-nav-bar="panel"]');
    if (
      drawer?.dataset.open === 'true' &&
      panelBar &&
      !panelBar.hidden &&
      panelTitle?.textContent?.trim() &&
      drawerRect &&
      drawerRect.width > 0
    ) {
      const titleRect = panelTitle.getBoundingClientRect();
      const titlePad = rtl
        ? Math.max(0, Math.round(drawerRect.right - titleRect.right))
        : Math.max(0, Math.round(titleRect.left - drawerRect.left));
      document.documentElement.style.setProperty('--mobile-nav-panel-title-pad', `${titlePad}px`);
    } else {
      document.documentElement.style.removeProperty('--mobile-nav-panel-title-pad');
    }
  };

  const updateStickyNav = () => {
    const utility = document.querySelector('[data-announcement-utility]');
    const primary = document.querySelector('[data-announcement-primary]');
    const utilityH = utility && !utility.classList.contains('is-hidden') ? utility.offsetHeight : 0;
    const pinned = isHeaderStackPinned();

    if (utilityBar) pinUtilityBar();
    else pinHeaderOnly();

    const primaryH = primary && !primary.classList.contains('is-hidden') && !primary.classList.contains('is-scrolled-away')
      ? primary.offsetHeight
      : 0;

    document.documentElement.style.setProperty('--ann-utility-top', '0px');
    document.documentElement.style.setProperty('--sticky-nav-top', pinned ? `${utilityH}px` : '0px');
    document.documentElement.style.setProperty('--announcement-h', `${primaryH + utilityH}px`);
    document.documentElement.style.setProperty('--ann-utility-h', `${utilityH}px`);

    if (header) {
      const overlay = header.classList.contains('site-header--overlay');
      let atTop = !pinned && window.scrollY <= 12;
      if (overlay && primaryBar && !primaryBar.classList.contains('is-hidden') && !primaryBar.classList.contains('is-scrolled-away')) {
        atTop = window.scrollY <= Math.max(12, primaryBar.offsetHeight - 4);
      }
      header.dataset.atTop = atTop ? 'true' : 'false';
      header.dataset.scrolled = atTop ? 'false' : 'true';
      document.documentElement.dataset.headerStuck = pinned ? 'true' : 'false';
    }

    updateMobileNavInset();
  };

  if (header || utilityBar || primaryBar) {
    document.addEventListener('scroll', updateStickyNav, { passive: true });
    updateStickyNav();
  }
  window.addEventListener('resize', updateMobileNavInset, { passive: true });
  updateMobileNavInset();

  document.addEventListener('ghalia:drawer:open', (e) => {
    if (!e.detail?.drawer?.hasAttribute('data-mobile-nav')) return;
    requestAnimationFrame(() => {
      syncMobileNavPad();
      requestAnimationFrame(syncMobileNavPad);
    });
  });

  /* ---------- Drawer / overlay coordination ---------- */
  const overlay = document.querySelector('[data-overlay]');

  const syncDrawerTriggers = () => {
    $$('[data-drawer-open], [data-drawer-toggle]').forEach((btn) => {
      const sel = btn.dataset.drawerOpen || btn.dataset.drawerToggle;
      if (!sel) return;
      const drawer = document.querySelector(sel);
      const open = drawer?.dataset.open === 'true';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (btn.hasAttribute('data-mobile-nav-toggle')) {
        const label = open ? btn.dataset.labelClose : btn.dataset.labelMenu;
        if (label) btn.setAttribute('aria-label', label);
      }
    });
  };

  let resetMobileNavView = null;

  const closeDrawers = () => {
    const hadMobileNav = document.querySelector('[data-mobile-nav][data-open="true"]');
    window.GhaliaCartQuickAdd?.close?.();
    cartDrawer?.classList.remove('is-quick-add-open');
    $$('.drawer[data-open="true"]').forEach((d) => {
      d.dataset.open = 'false';
      d.setAttribute('aria-hidden', 'true');
    });
    if (overlay) overlay.dataset.open = 'false';
    document.documentElement.classList.remove('drawer-open');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    syncDrawerTriggers();
    if (hadMobileNav) resetMobileNavView?.();
  };

  const isMobileNavDrawer = (el) => el?.hasAttribute('data-mobile-nav');

  const shouldSkipDrawerOverlay = (drawer) => {
    if (!isMobileNavViewport()) return false;
    if (isMobileNavDrawer(drawer)) return true;
    if (drawer?.hasAttribute('data-size-drawer')) return true;
    return false;
  };

  const openDrawer = (drawer) => {
    if (!drawer) return;
    $$('.drawer[data-open="true"]').forEach((d) => {
      if (d !== drawer) {
        d.dataset.open = 'false';
        d.setAttribute('aria-hidden', 'true');
      }
    });
    drawer.dataset.open = 'true';
    drawer.setAttribute('aria-hidden', 'false');
    const skipOverlay = shouldSkipDrawerOverlay(drawer);
    if (overlay) overlay.dataset.open = skipOverlay ? 'false' : 'true';
    document.documentElement.classList.add('drawer-open');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    syncDrawerTriggers();
    updateMobileNavInset();
    document.dispatchEvent(new CustomEvent('ghalia:drawer:open', { detail: { drawer } }));
    if (isMobileNavDrawer(drawer) && isMobileNavViewport()) {
      requestAnimationFrame(() => {
        syncMobileNavPad();
        requestAnimationFrame(syncMobileNavPad);
      });
      const toggle = document.querySelector('[data-mobile-nav-toggle]');
      toggle?.focus({ preventScroll: true });
    } else {
      const focusable = drawer.querySelector(
        '[data-drawer-close], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus({ preventScroll: true });
    }
  };

  const syncOverlayWithOpenDrawer = () => {
    const openDrawerEl = document.querySelector('.drawer[data-open="true"]');
    if (!openDrawerEl) {
      if (overlay?.dataset.open === 'true') overlay.dataset.open = 'false';
      return;
    }
    if (overlay) overlay.dataset.open = shouldSkipDrawerOverlay(openDrawerEl) ? 'false' : 'true';
  };

  syncOverlayWithOpenDrawer();
  window.addEventListener('pageshow', syncOverlayWithOpenDrawer);

  const wishlistDrawerEl = () => document.querySelector('[data-wishlist-drawer]');
  const openWishlistDrawer = () => {
    if (typeof window.GhaliaWishlistOpen === 'function') {
      window.GhaliaWishlistOpen();
      return;
    }
    const drawer = wishlistDrawerEl();
    if (drawer) openDrawer(drawer);
  };

  window.GhaliaUI = window.GhaliaUI || {};
  window.GhaliaUI.openWishlistDrawer = openWishlistDrawer;

  const isWishlistUrl = (href) => {
    if (!href) return false;
    try {
      const path = href.startsWith('http') ? new URL(href, window.location.origin).pathname : href;
      return /\/pages\/(wishlist|swym-wishlist|swym)(\/|$)/i.test(path);
    } catch (_) {
      return /\/pages\/(wishlist|swym)/i.test(href);
    }
  };

  const ensureWishlistHeaderOpensDrawer = () => {
    let btn = document.querySelector('[data-wishlist-header-btn]');
    if (!btn) {
      const swymBtn = document.querySelector(
        '.site-actions__btn--wishlist a, .site-actions__btn--wishlist.swym-wishlist, a.swym-wishlist'
      );
      if (swymBtn) btn = swymBtn;
    }
    if (!btn || btn.matches('[data-drawer-open]')) return;

    if (btn.tagName === 'A') {
      const replacement = document.createElement('button');
      replacement.type = 'button';
      replacement.className = btn.className;
      replacement.innerHTML = btn.innerHTML;
      replacement.setAttribute('data-wishlist-header-btn', '');
      replacement.setAttribute('data-drawer-open', '[data-wishlist-drawer]');
      replacement.setAttribute('aria-label', btn.getAttribute('aria-label') || 'Open wishlist');
      btn.replaceWith(replacement);
      return;
    }

    btn.setAttribute('data-drawer-open', '[data-wishlist-drawer]');
    if (btn.tagName === 'A') btn.removeAttribute('href');
  };

  document.addEventListener(
    'click',
    (e) => {
      const link = e.target.closest(
        'a[href*="/pages/wishlist"], a[href*="/pages/swym-wishlist"], a[href*="/pages/swym"], a.swym-wishlist, .swym-wishlist'
      );
      if (!link || link.hasAttribute('data-drawer-close')) return;
      const href = link.getAttribute('href') || '';
      if (!isWishlistUrl(href) && !link.classList.contains('swym-wishlist')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openWishlistDrawer();
    },
    true
  );

  const handleDrawerClick = (e) => {
    const toggle = e.target.closest('[data-drawer-toggle]');
    if (toggle) {
      e.preventDefault();
      const drawer = document.querySelector(toggle.dataset.drawerToggle);
      if (drawer?.dataset.open === 'true') {
        closeDrawers();
      } else {
        openDrawer(drawer);
      }
      return;
    }

    const closerOnly = e.target.closest('[data-drawer-close]:not([data-drawer-open])');
    if (closerOnly) {
      e.preventDefault();
      closeDrawers();
      return;
    }

    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      if (opener.dataset.drawerOpen === '[data-wishlist-drawer]') {
        openWishlistDrawer();
        return;
      }
      const drawer = document.querySelector(opener.dataset.drawerOpen);
      if (!drawer) return;
      if (drawer.dataset.open === 'true') {
        closeDrawers();
        return;
      }
      if (drawer.hasAttribute('data-size-drawer') && opener.dataset.sizeSegment) {
        drawer.dataset.activeSegment = opener.dataset.sizeSegment;
      }
      if (drawer.hasAttribute('data-care-drawer') && opener.dataset.careSegment) {
        drawer.dataset.activeSegment = opener.dataset.careSegment;
      }
      if (drawer.hasAttribute('data-usp-policy-drawer') && opener.dataset.uspPanel) {
        const panelId = opener.dataset.uspPanel;
        drawer.querySelectorAll('[data-usp-panel]').forEach((panel) => {
          const on = panel.dataset.uspPanel === panelId;
          panel.hidden = !on;
        });
        const titleEl = drawer.querySelector('[data-usp-drawer-title]');
        const activePanel = drawer.querySelector(`[data-usp-panel="${panelId}"]`);
        if (titleEl && activePanel) {
          const titleText = activePanel.dataset.uspDrawerTitleText;
          const heading = activePanel.querySelector('.usp-policy-drawer__heading');
          if (titleText) titleEl.textContent = decodeHtmlEntities(titleText);
          else if (heading) titleEl.textContent = heading.textContent.trim();
        }
        if (panelId === 'shipping' && activePanel) {
          const shippingRoot = activePanel.querySelector('[data-usp-shipping-regions]');
          if (shippingRoot) {
            shippingRoot.querySelectorAll('[data-shipping-region]').forEach((btn) => {
              const on = btn.dataset.shippingRegion === 'ksa';
              btn.classList.toggle('is-active', on);
              btn.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            shippingRoot.querySelectorAll('[data-shipping-region-pane]').forEach((pane) => {
              pane.hidden = pane.dataset.shippingRegionPane !== 'ksa';
            });
          }
        }
      }
      closeDrawers();
      openDrawer(drawer);
      return;
    }

    if (e.target.closest('[data-overlay]')) {
      closeDrawers();
      return;
    }

    /* Mobile menu: no dim overlay — close when tapping the visible page strip or header */
    const mobileNavOpen = document.querySelector('[data-mobile-nav][data-open="true"]');
    if (mobileNavOpen && isMobileNavViewport()) {
      if (
        !e.target.closest('[data-mobile-nav]') &&
        !e.target.closest('[data-mobile-nav-toggle]')
      ) {
        closeDrawers();
      }
    }
  };

  /* ---------- Size guide drawer + page ---------- */
  const initSizeGuide = (root) => {
    if (!root) return;

    const types = ['rings', 'bracelets', 'necklaces', 'earrings'];
    let activeType = 'rings';
    let activeTab = 'guide';

    const hash = window.location.hash.replace('#', '');
    if (root.hasAttribute('data-size-guide-page') && types.includes(hash)) {
      activeType = hash;
    }

    const updateSizeGuide = () => {
      root.querySelectorAll('[data-size-type]').forEach((chip) => {
        const on = chip.dataset.sizeType === activeType;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-size-tab]').forEach((tab) => {
        const on = tab.dataset.sizeTab === activeTab;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      const chips = root.querySelector('.size-drawer__chips, .size-guide-page__chips');
      if (chips) chips.hidden = activeTab !== 'guide';
      root.querySelectorAll('[data-size-pane]').forEach((pane) => {
        pane.hidden = !(pane.dataset.sizePane === activeType && pane.dataset.sizeMode === activeTab);
      });
    };

    root.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-size-type]');
      if (chip && root.contains(chip)) {
        activeType = chip.dataset.sizeType;
        if (root.hasAttribute('data-size-guide-page')) {
          const nextHash = `#${activeType}`;
          if (window.location.hash !== nextHash) {
            history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
          }
        }
        updateSizeGuide();
        return;
      }
      const tab = e.target.closest('[data-size-tab]');
      if (tab && root.contains(tab)) {
        activeTab = tab.dataset.sizeTab;
        updateSizeGuide();
      }
    });

    if (root.hasAttribute('data-size-drawer')) {
      document.addEventListener('ghalia:drawer:open', (e) => {
        if (e.detail?.drawer !== root) return;
        const segment = root.dataset.activeSegment;
        if (segment && root.querySelector(`[data-size-type="${segment}"]`)) {
          activeType = segment;
          activeTab = 'guide';
        }
        updateSizeGuide();
      });
    }

    updateSizeGuide();
  };

  initSizeGuide(document.querySelector('[data-size-drawer]'));
  initSizeGuide(document.querySelector('[data-size-guide-page]'));

  /* ---------- Jewelry care drawer + page ---------- */
  const initCareGuide = (root) => {
    if (!root) return;

    const materialTypes = ['vermeil', 'sterling', 'pearls'];
    let activeTab = 'essentials';
    let activeMaterial = 'vermeil';

    const hash = window.location.hash.replace('#', '');
    if (root.hasAttribute('data-care-guide-page')) {
      if (['essentials', 'materials', 'avoid'].includes(hash)) {
        activeTab = hash;
      }
      if (materialTypes.includes(hash)) {
        activeTab = 'materials';
        activeMaterial = hash;
      }
    }

    const updateCareGuide = () => {
      root.querySelectorAll('[data-care-tab]').forEach((tab) => {
        const on = tab.dataset.careTab === activeTab;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      const chips = root.querySelector('.care-drawer__chips, .care-guide-page__chips');
      if (chips) chips.hidden = activeTab !== 'materials';
      root.querySelectorAll('[data-care-type]').forEach((chip) => {
        const on = chip.dataset.careType === activeMaterial;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-care-pane]').forEach((pane) => {
        const paneId = pane.dataset.carePane;
        let show = false;
        if (activeTab === 'essentials' && paneId === 'essentials') show = true;
        if (activeTab === 'avoid' && paneId === 'avoid') show = true;
        if (activeTab === 'materials' && paneId === activeMaterial) show = true;
        pane.hidden = !show;
      });
    };

    root.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-care-tab]');
      if (tab && root.contains(tab)) {
        activeTab = tab.dataset.careTab;
        if (root.hasAttribute('data-care-guide-page')) {
          const nextHash = `#${activeTab}`;
          if (window.location.hash !== nextHash) {
            history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
          }
        }
        updateCareGuide();
        return;
      }
      const chip = e.target.closest('[data-care-type]');
      if (chip && root.contains(chip)) {
        activeMaterial = chip.dataset.careType;
        activeTab = 'materials';
        if (root.hasAttribute('data-care-guide-page')) {
          const nextHash = `#${activeMaterial}`;
          if (window.location.hash !== nextHash) {
            history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
          }
        }
        updateCareGuide();
      }
    });

    if (root.hasAttribute('data-care-drawer')) {
      document.addEventListener('ghalia:drawer:open', (e) => {
        if (e.detail?.drawer !== root) return;
        const segment = root.dataset.activeSegment;
        if (segment === 'avoid') {
          activeTab = 'avoid';
        } else if (segment === 'materials' || materialTypes.includes(segment)) {
          activeTab = 'materials';
          if (materialTypes.includes(segment)) activeMaterial = segment;
        } else {
          activeTab = 'essentials';
        }
        updateCareGuide();
      });
    }

    updateCareGuide();
  };

  initCareGuide(document.querySelector('[data-care-drawer]'));
  initCareGuide(document.querySelector('[data-care-guide-page]'));

  /* ---------- USP policy — shipping region pills (drawer + policy pages) ---------- */
  document.querySelectorAll('[data-usp-shipping-regions]').forEach((root) => {
    root.addEventListener('click', (e) => {
      const regionBtn = e.target.closest('[data-shipping-region]');
      if (!regionBtn || !root.contains(regionBtn)) return;
      const regionId = regionBtn.dataset.shippingRegion;
      root.querySelectorAll('[data-shipping-region]').forEach((btn) => {
        const on = btn.dataset.shippingRegion === regionId;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-shipping-region-pane]').forEach((pane) => {
        pane.hidden = pane.dataset.shippingRegionPane !== regionId;
      });
    });
  });

  document.addEventListener('click', handleDrawerClick);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawers(); });

  document.addEventListener('click', (e) => {
    if (e.target.closest('.mobile-nav a[href]')) closeDrawers();
  });

  /* ---------- Mobile nav sub-panels ---------- */
  const mobileNavEl = document.querySelector('[data-mobile-nav]');
  if (mobileNavEl) {
    const mobileNavRootBar = mobileNavEl.querySelector('[data-mobile-nav-bar="root"]');
    const mobileNavPanelBar = mobileNavEl.querySelector('[data-mobile-nav-bar="panel"]');
    const mobileNavPanelTitle = mobileNavEl.querySelector('[data-mobile-nav-panel-title]');
    const mobileNavViews = () => Array.from(mobileNavEl.querySelectorAll('[data-mobile-nav-view]'));

    const setMobileNavView = (viewId) => {
      const id = viewId || 'root';
      mobileNavEl.dataset.mobileNavActiveView = id;
      mobileNavViews().forEach((view) => {
        const active = view.dataset.mobileNavView === id;
        view.hidden = !active;
        view.classList.toggle('is-active', active);
      });
      const isRoot = id === 'root';
      if (mobileNavRootBar) mobileNavRootBar.hidden = !isRoot;
      if (mobileNavPanelBar) mobileNavPanelBar.hidden = isRoot;
      mobileNavEl.querySelectorAll('[data-mobile-nav-open]').forEach((btn) => {
        btn.setAttribute('aria-expanded', btn.getAttribute('data-mobile-nav-open') === id ? 'true' : 'false');
      });
      if (!isRoot) {
        requestAnimationFrame(() => {
          syncMobileNavPad();
          requestAnimationFrame(syncMobileNavPad);
        });
      }
    };

    resetMobileNavView = () => setMobileNavView('root');
    setMobileNavView('root');

    mobileNavEl.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-mobile-nav-open]');
      if (openBtn) {
        e.preventDefault();
        const panelId = openBtn.getAttribute('data-mobile-nav-open');
        const title = openBtn.getAttribute('data-mobile-nav-title') || openBtn.textContent?.trim() || '';
        if (mobileNavPanelTitle) mobileNavPanelTitle.textContent = title;
        if (mobileNavEl.querySelector(`[data-mobile-nav-view="${panelId}"]`)) {
          setMobileNavView(panelId);
        }
        return;
      }
      if (e.target.closest('[data-mobile-nav-back]')) {
        e.preventDefault();
        setMobileNavView('root');
      }
    });

    document.addEventListener('ghalia:drawer:open', (e) => {
      if (e.detail?.drawer?.hasAttribute('data-mobile-nav')) setMobileNavView('root');
    });
  }

  syncDrawerTriggers();

  /* ---------- Mega menu (hover / click — desktop) ---------- */
  const megaItems = header ? $$('.site-nav__item--has-mega', header) : [];
  const megaPanels = header ? $$('[data-mega-panel]', header) : [];
  const megaPanelsWrap = header?.querySelector('[data-mega-panels]');
  const isDesktopNav = () => window.matchMedia('(min-width: 1025px)').matches;

  const navIdFor = (el) => el?.getAttribute('data-nav-id') ?? '';
  const panelIdFor = (el) => el?.getAttribute('data-mega-panel') ?? '';
  const categoryIdFor = (el) => el?.getAttribute('data-category-id') ?? '';

  const panelForItem = (item) => {
    const navId = navIdFor(item);
    const catId = categoryIdFor(item);
    return megaPanels.find((panel) => {
      if (panelIdFor(panel) === navId) return true;
      if (catId && categoryIdFor(panel) === catId) return true;
      return false;
    });
  };

  const itemForPanel = (panel) => {
    const panelId = panelIdFor(panel);
    const catId = categoryIdFor(panel);
    return megaItems.find((navItem) => {
      if (navIdFor(navItem) === panelId) return true;
      if (catId && categoryIdFor(navItem) === catId) return true;
      return false;
    });
  };

  const syncMegaPanels = () => {
    megaPanels.forEach((panel) => {
      const item = itemForPanel(panel);
      panel.dataset.open = item?.dataset.open === 'true' ? 'true' : 'false';
    });
    if (megaPanelsWrap) {
      const open = megaItems.some((item) => item.dataset.open === 'true');
      megaPanelsWrap.setAttribute('aria-hidden', open ? 'false' : 'true');
    }
  };

  const syncMegaHeader = () => {
    if (!header) return;
    const open = megaItems.some((item) => item.dataset.open === 'true');
    header.dataset.megaOpen = open ? 'true' : 'false';
    header.classList.toggle('site-header--mega-open', open);
    document.documentElement.classList.toggle('is-mega-open', open);
    syncMegaPanels();
  };

  const closeAllMegas = () => {
    megaItems.forEach((item) => {
      item.dataset.open = 'false';
    });
    syncMegaHeader();
  };

  if (megaItems.length && header) {
    let closeTimer;

    const openItem = (item) => {
      if (!isDesktopNav()) return;
      clearTimeout(closeTimer);
      megaItems.forEach((other) => {
        if (other !== item) other.dataset.open = 'false';
      });
      item.dataset.open = 'true';
      syncMegaHeader();
    };

    const siteNav = header.querySelector('.site-nav');
    const resolveMegaItem = (target) => {
      if (!(target instanceof Element)) return null;
      const item = target.closest('.site-nav__item--has-mega');
      return item && header.contains(item) ? item : null;
    };

    const bindMegaHover = (item) => {
      const link = item.querySelector('.site-nav__link');
      const openThisItem = () => openItem(item);
      item.addEventListener('mouseenter', openThisItem);
      link?.addEventListener('mouseenter', openThisItem);
      link?.addEventListener('focusin', openThisItem);
      panelForItem(item)?.addEventListener('mouseenter', openThisItem);
    };

    megaItems.forEach(bindMegaHover);

    siteNav?.addEventListener('focusin', (e) => {
      const item = resolveMegaItem(e.target);
      if (item) openItem(item);
    });

    megaPanelsWrap?.addEventListener('mouseover', (e) => {
      const panel = e.target.closest('[data-mega-panel]');
      if (!panel) return;
      const item = itemForPanel(panel);
      if (item) openItem(item);
    });

    header.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    header.addEventListener('mouseleave', (e) => {
      if (!isDesktopNav()) return;
      if (e.relatedTarget instanceof Node && header.contains(e.relatedTarget)) return;
      closeTimer = setTimeout(closeAllMegas, 180);
    });

    document.addEventListener('click', (e) => {
      if (!isDesktopNav()) return;
      if (e.target.closest('[data-site-header]')) return;
      if (megaItems.some((item) => item.dataset.open === 'true')) closeAllMegas();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllMegas();
    });

    syncMegaHeader();
  }

  /* ---------- Cart drawer ---------- */
  const cartDrawer = document.querySelector('[data-cart-drawer]');

  const showGhaliaToast = (message) => {
    if (!message) return;
    let toast = document.querySelector('[data-ghalia-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ghalia-toast';
      toast.setAttribute('data-ghalia-toast', '');
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.dataset.visible = 'true';
    clearTimeout(showGhaliaToast._timer);
    showGhaliaToast._timer = window.setTimeout(() => {
      toast.dataset.visible = 'false';
    }, 2600);
  };

  let suppressDrawerRefreshHandler = false;
  const cartLineMutations = new Map();
  const variantStockCache = new Map();

  const getCartLineRoot = (key) =>
    cartDrawer?.querySelector(`[data-cart-line-key="${CSS.escape(key)}"]`) ||
    document.querySelector(`[data-cart-line-key="${CSS.escape(key)}"]`);

  const pruneRemovedCartLines = (cart) => {
    const drawer = cartDrawer || document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const keys = new Set((cart?.items || []).map((item) => item.key));
    drawer.querySelectorAll('[data-cart-line-key]').forEach((line) => {
      if (!keys.has(line.dataset.cartLineKey)) line.remove();
    });
  };

  const setCartLineBusy = (key, busy) => {
    const line = getCartLineRoot(key);
    if (!line) return;
    line.classList.toggle('cart-line--busy', busy);
    line.querySelectorAll('[data-qty-step], [data-cart-line-remove], [data-cart-line-qty]').forEach((el) => {
      el.disabled = busy;
    });
  };

  const cloneCart = (cart) => {
    if (!cart?.items) return { items: [], item_count: 0, total_price: 0 };
    return JSON.parse(JSON.stringify(cart));
  };

  const patchCartLineQuantity = (cart, key, quantity) => {
    const next = cloneCart(cart);
    const idx = next.items.findIndex((item) => item.key === key);
    if (idx === -1) return next;
    if (quantity <= 0) {
      next.items.splice(idx, 1);
    } else {
      const item = next.items[idx];
      const prevQty = Math.max(1, Number(item.quantity) || 1);
      const scale = quantity / prevQty;
      ['line_price', 'final_line_price', 'original_line_price', 'total_discount'].forEach((field) => {
        if (item[field] != null) item[field] = Math.round(Number(item[field]) * scale);
      });
      if (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length) {
        item.line_level_discount_allocations = item.line_level_discount_allocations.map((alloc) => ({
          ...alloc,
          amount: Math.round((Number(alloc.amount) || 0) * scale),
        }));
      }
      item.quantity = quantity;
    }
    next.item_count = next.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    let subtotal = 0;
    let discount = 0;
    next.items.forEach((item) => {
      subtotal += Number(item.final_line_price ?? item.line_price) || 0;
      discount += Number(item.total_discount) || 0;
    });
    next.items_subtotal_price = subtotal;
    next.total_price = subtotal;
    next.total_discount = discount;
    return next;
  };

  let cartPricingRaf = null;

  const scheduleCartPricing = (cart) => {
    if (cartPricingRaf) cancelAnimationFrame(cartPricingRaf);
    cartPricingRaf = requestAnimationFrame(() => {
      cartPricingRaf = null;
      document.dispatchEvent(new CustomEvent('ghalia:cart:pricing', { detail: { cart } }));
    });
  };

  const paintAllCartLinePrices = (cart) => {
    (cart?.items || []).forEach((item) => paintCartLinePrice(cart, item.key));
  };

  const paintCartLinePrice = (cart, key) => {
    const item = (cart?.items || []).find((row) => row.key === key);
    const line = getCartLineRoot(key);
    const pricing = window.GhaliaPricing;
    if (!item || !line || !pricing) return;
    const currentEl = line.querySelector('[data-cart-line-current]');
    const compareEl = line.querySelector('[data-cart-line-compare]');
    const originalNet = Number(item.original_line_price) || Number(item.line_price) || 0;
    const allocNet = (item.line_level_discount_allocations || []).reduce(
      (sum, alloc) => sum + (Number(alloc.amount) || 0),
      0
    );
    const finalNet =
      allocNet > 0
        ? Math.max(0, originalNet - allocNet)
        : Number(item.final_line_price) || originalNet;
    if (currentEl) {
      const nextPrice = pricing.formatCustomerLineNet
        ? pricing.formatCustomerLineNet(finalNet, line)
        : pricing.formatNet(finalNet);
      if (currentEl.textContent.trim() !== nextPrice.trim()) {
        currentEl.textContent = nextPrice;
      }
    }
    if (compareEl) {
      if (originalNet > finalNet && originalNet > 0) {
        const compareText = pricing.formatCustomerLineNet
          ? pricing.formatCustomerLineNet(originalNet, line)
          : pricing.formatNet(originalNet);
        compareEl.textContent = compareText;
        compareEl.hidden = false;
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.hidden = true;
        compareEl.setAttribute('hidden', '');
      }
    }
  };

  const catalogNetFromCart = (cart, drawer) => {
    const pricing = window.GhaliaPricing;
    if (!pricing || !drawer || !cart?.items) return 0;
    let net = 0;
    cart.items.forEach((item) => {
      if (pricing.isFreeGiftLine?.(item)) return;
      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      const fromDom = Number(row?.dataset?.cartLineNet || 0);
      net +=
        fromDom > 0
          ? fromDom
          : Number(item.original_line_price) || Number(item.line_price) || 0;
    });
    return net;
  };

  const patchStackStateNetFromCart = (cart) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const prev = window.GhaliaCart?.lastStackState || {};
    if (!drawer || !cart?.items) return;
    const net = catalogNetFromCart(cart, drawer);
    window.GhaliaCart = window.GhaliaCart || {};
    window.GhaliaCart.lastStackState = { ...prev, netSubtotalCents: net };
  };

  const refreshCartSummaryLight = (cart) => {
    patchStackStateNetFromCart(cart);
    const state = window.GhaliaCart?.lastStackState;
    const drawer = document.querySelector('[data-cart-drawer]');
    if (cart && state && drawer && window.GhaliaCartSummary?.apply) {
      window.GhaliaCartSummary.apply(cart, state, drawer);
    }
  };

  const applyCartLocally = (cart, options = {}) => {
    if (!cart) return;
    if (window.GhaliaCart) window.GhaliaCart.lastCart = cart;
    renderCart(cart);
    if (options.skipPricing) {
      refreshCartSummaryLight(cart);
    } else {
      scheduleCartPricing(cart);
    }
    if (options.promoUi !== false) syncCartPromoUI(cart);
    syncCartCheckoutState();
  };

  const optimisticCartLineQty = (key, qty) => {
    const line = getCartLineRoot(key);
    if (!line) return;
    const input = line.querySelector('[data-cart-line-qty]');
    const prevQty = Math.max(1, parseInt(input?.value, 10) || 1);
    if (input) input.value = String(qty);
    if (qty > 0 && prevQty > 0 && qty !== prevQty) {
      const scale = qty / prevQty;
      const net = Number(line.dataset.cartLineNet || 0);
      const display = Number(line.dataset.cartLineDisplay || 0);
      if (net) line.dataset.cartLineNet = String(Math.round(net * scale));
      if (display) line.dataset.cartLineDisplay = String(Math.round(display * scale));
    }
    line.classList.toggle('cart-line--removing', qty <= 0);
  };

  const needsFullDrawerRefresh = (before, after) => {
    if (!after?.items?.length) return true;
    const beforeKeys = new Set((before?.items || []).map((item) => item.key));
    for (const item of after.items) {
      if (!beforeKeys.has(item.key)) return true;
    }
    return false;
  };

  const formatThresholdMoney = (cents) => {
    const P = window.GhaliaPricing;
    if (P?.formatNet) return P.formatNet(cents);
    return (Number(cents) / 100).toFixed(0);
  };

  const updateGiftBanner = (cart) => {
    const banner = cartDrawer?.querySelector('[data-cart-gift-banner]');
    if (!banner) return;
    const config = getCartGiftConfig();
    if (!config) return;
    const qualifying = getQualifyingCartTotal(cart, config);
    const threshold = config.threshold;
    const msg = banner.querySelector('.cart-gift__msg');
    const hasPaid = (cart?.items || []).some((item) => !isFreeGiftLine(item, config));
    const unlocked = config.alwaysGift ? hasPaid : qualifying >= threshold;
    banner.classList.toggle('is-unlocked', unlocked);
    if (!msg) return;
    if (unlocked) {
      msg.innerHTML = banner.dataset.giftUnlockedHtml || msg.innerHTML;
      return;
    }
    const remaining = Math.max(0, threshold - qualifying);
    const template = banner.dataset.giftSpendTemplate || '';
    msg.innerHTML = template.replace('__AMOUNT__', formatThresholdMoney(remaining));
  };

  const patchCartGiftState = (cart) => {
    const config = getCartGiftConfig();
    if (!config || !cart?.items) return cart;
    const giftLine = findFreeGiftLine(cart, config);
    const qualifying = getQualifyingCartTotal(cart, config);
    const hasPaid = cart.items.some((item) => !isFreeGiftLine(item, config));
    const shouldHave = config.alwaysGift ? hasPaid : qualifying >= config.threshold;
    if (shouldHave || !giftLine) return cart;
    const next = cloneCart(cart);
    next.items = next.items.filter((item) => item.key !== giftLine.key);
    next.item_count = cartItemTotal(next);
    getCartLineRoot(giftLine.key)?.remove();
    return next;
  };

  let cartEmptyTemplateHtml = '';

  const cacheCartEmptyTemplate = () => {
    if (cartEmptyTemplateHtml) return;
    const tpl = cartDrawer?.querySelector('[data-cart-empty-template]');
    if (tpl) cartEmptyTemplateHtml = tpl.innerHTML.trim();
  };

  const showCartDrawerEmpty = () => {
    const body = cartDrawer?.querySelector('[data-cart-drawer-body]');
    if (!body) return;
    cacheCartEmptyTemplate();
    cartDrawer?.querySelector('[data-cart-drawer-footer], .drawer__footer')?.remove();
    if (cartEmptyTemplateHtml) {
      body.innerHTML = cartEmptyTemplateHtml;
      return;
    }
    if (body.querySelector('.cart-empty')) return;
    const emptyCopy = window.Shopify?.strings?.cart?.empty || 'Your bag is empty.';
    const continueCopy = window.Shopify?.strings?.cart?.continueShopping || 'Continue shopping';
    const root = window.Shopify?.routes?.root || '/';
    body.innerHTML = `<div class="cart-empty"><p>${emptyCopy}</p><a class="btn btn--primary" href="${root}collections" data-drawer-close>${continueCopy}</a></div>`;
  };

  const applyOptimisticCartState = (cart, options = {}) => {
    const next = patchCartGiftState(cart);
    pruneRemovedCartLines(next);
    updateGiftBanner(next);
    updateDrawerTitle(next);
    if (!next?.items?.length) showCartDrawerEmpty();
    applyCartLocally(next, { ...options, skipPricing: false, promoUi: false });
    return next;
  };

  const updateDrawerTitle = (cart) => {
    const title = cartDrawer?.querySelector('.drawer__title');
    if (!title) return;
    const count = getCartDisplayCount(cart);
    const template = title.dataset.titleTemplate || title.textContent.replace(/\d+/, '__COUNT__');
    if (!title.dataset.titleTemplate && /\d+/.test(title.textContent)) {
      title.dataset.titleTemplate = title.textContent.replace(/\d+/, '__COUNT__');
    }
    if (title.dataset.titleTemplate) {
      title.textContent = title.dataset.titleTemplate.replace('__COUNT__', String(count));
    }
  };

  const syncCartCheckoutState = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const banner = drawer.querySelector('[data-cart-oos-banner]');
    const checkoutBtn = drawer.querySelector('.cart-checkout');
    const hasOos = drawer.querySelector('[data-cart-line-available="false"]') != null;
    if (banner) banner.hidden = !hasOos;
    if (checkoutBtn) {
      checkoutBtn.disabled = hasOos;
      checkoutBtn.setAttribute('aria-disabled', hasOos ? 'true' : 'false');
    }
  };

  const fetchVariantStock = async (variantId) => {
    const id = String(variantId);
    if (variantStockCache.has(id)) return variantStockCache.get(id);
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}variants/${id}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Could not verify availability');
    const data = await res.json();
    const stock = {
      available: Boolean(data.available),
      inventoryManagement: data.inventory_management,
      inventoryQuantity: Number(data.inventory_quantity),
    };
    variantStockCache.set(id, stock);
    return stock;
  };

  const assertVariantCanAdd = async (variantId, quantity = 1) => {
    const stock = await fetchVariantStock(variantId);
    const soldOut = window.Shopify?.strings?.cart?.soldOut || 'Sold out';
    if (!stock.available) throw new Error(soldOut);
    if (
      stock.inventoryManagement === 'shopify' &&
      Number.isFinite(stock.inventoryQuantity) &&
      quantity > stock.inventoryQuantity
    ) {
      throw new Error(soldOut);
    }
    return stock;
  };

  const getCartMaxItems = () => {
    const max = parseInt(cartDrawer?.dataset.cartMaxItems || '0', 10);
    return Number.isFinite(max) && max > 0 ? max : 0;
  };

  const cartItemTotal = (cart) =>
    (cart?.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  let cartReconcileChain = Promise.resolve();

  const promoAllocationNet = (cart) =>
    (cart?.items || []).reduce(
      (sum, item) =>
        sum +
        (item.line_level_discount_allocations || []).reduce(
          (lineSum, alloc) => lineSum + (Number(alloc.amount) || 0),
          0
        ),
      0
    );

  const promoNeedsRestore = (cart) => {
    const code = storedPromoCode() || defaultPromoCode();
    if (!code) return false;
    if (!cartPromoMeetsMinimum(cart)) return false;
    if (cartPromoCode(cart)) return false;
    if (promoAllocationNet(cart) > 0) return false;
    if (Number(cart?.total_discount) > 0) return false;
    return true;
  };

  const giftStateChanged = (before, after) => {
    const config = getCartGiftConfig();
    if (!config) return false;
    const hadGift = Boolean(findFreeGiftLine(before, config));
    const hasGift = Boolean(findFreeGiftLine(after, config));
    if (hadGift !== hasGift) return true;
    const qBefore = getQualifyingCartTotal(before, config);
    const qAfter = getQualifyingCartTotal(after, config);
    const shouldBefore = config.alwaysGift
      ? (before?.items || []).some((item) => !isFreeGiftLine(item, config))
      : qBefore >= config.threshold;
    const shouldAfter = config.alwaysGift
      ? (after?.items || []).some((item) => !isFreeGiftLine(item, config))
      : qAfter >= config.threshold;
    return shouldBefore !== shouldAfter;
  };

  const applyCartLineChangeFast = (beforeCart, synced) => {
    let next = patchCartGiftState(synced);
    pruneRemovedCartLines(next);
    updateGiftBanner(next);
    updateDrawerTitle(next);
    if (!next?.items?.length) showCartDrawerEmpty();
    applyCartLocally(next);
    return next;
  };

  const reconcileCartAfterLineChange = (beforeCart, synced) => {
    cartReconcileChain = cartReconcileChain
      .then(async () => {
        let latest = synced;
        if (giftStateChanged(beforeCart, latest)) {
          latest = await syncCartFreeGift(latest);
          if (needsFullDrawerRefresh(beforeCart, latest)) {
            await processCartUpdate(latest, { refresh: 'full' });
            return;
          }
        }
        if (promoNeedsRestore(latest)) {
          latest = await syncCartPromoEligibility(latest, { retries: 1 });
        }
        if (latest !== synced || giftStateChanged(beforeCart, latest)) {
          applyCartLineChangeFast(beforeCart, latest);
        }
      })
      .catch((err) => console.error(err));
    return cartReconcileChain;
  };

  const clearCartLineMutationTimer = (pending) => {
    if (!pending) return;
    if (pending.timer) clearTimeout(pending.timer);
    if (pending.raf) cancelAnimationFrame(pending.raf);
    pending.timer = null;
    pending.raf = null;
  };

  const cartLineAbort = new Map();

  const commitCartLineChange = async (key, quantity, options = {}) => {
    cartLineAbort.get(key)?.abort();
    const abort = new AbortController();
    cartLineAbort.set(key, abort);
    clearCartLineMutationTimer(cartLineMutations.get(key));
    let pending = cartLineMutations.get(key);
    if (!pending) {
      pending = { targetQty: quantity, timer: null, raf: null, committing: false };
      cartLineMutations.set(key, pending);
    } else {
      pending.targetQty = quantity;
    }
    if (pending.committing) return;
    pending.committing = true;

    const beforeCart = window.GhaliaCart?.lastCart;
    const root = window.Shopify?.routes?.root || '/';

    try {
      while (true) {
        const qty = pending.targetQty;
        const maxItems = getCartMaxItems();
        if (maxItems > 0 && qty > 0) {
          const line = (beforeCart?.items || []).find((item) => item.key === key);
          const lineQty = Number(line?.quantity) || 0;
          const otherTotal = cartItemTotal(beforeCart) - lineQty;
          if (otherTotal + qty > maxItems) {
            showGhaliaToast(`Bag limit is ${maxItems} items`);
            const allowed = Math.max(0, maxItems - otherTotal);
            optimisticCartLineQty(key, allowed);
            const reverted = patchCartLineQuantity(window.GhaliaCart?.lastCart, key, allowed);
            applyCartLocally(reverted, { promoUi: false });
            break;
          }
        }

        const res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
          signal: abort.signal,
        });
        if (!res.ok) {
          let message = 'Could not update bag';
          try {
            const err = await res.json();
            message = err.description || err.message || message;
          } catch (_) { /* noop */ }
          throw new Error(message);
        }
        variantStockCache.clear();
        const synced = await res.json();
        if (!synced?.items?.length) {
          applyCartLineChangeFast(beforeCart, synced);
          reconcileCartAfterLineChange(beforeCart, synced);
        } else if (needsFullDrawerRefresh(beforeCart, synced)) {
          await processCartUpdate(synced, { refresh: 'full' });
        } else {
          applyCartLineChangeFast(beforeCart, synced);
          reconcileCartAfterLineChange(beforeCart, synced);
        }
        if (pending.targetQty === qty) break;
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error(err);
      showGhaliaToast(err.message || 'Could not update bag');
      try {
        const synced = await fetchCart();
        await processCartUpdate(synced, { refresh: 'full' });
      } catch (_) { /* noop */ }
    } finally {
      pending.committing = false;
      cartLineMutations.delete(key);
      if (cartLineAbort.get(key) === abort) cartLineAbort.delete(key);
      setCartLineBusy(key, false);
      getCartLineRoot(key)?.classList.remove('cart-line--removing');
    }
  };

  const queueCartLineChange = (key, quantity) => {
    if (quantity <= 0) {
      removeCartLineNow(key);
      return;
    }

    const maxItems = getCartMaxItems();
    if (maxItems > 0 && quantity > 0) {
      const cart = window.GhaliaCart?.lastCart;
      const line = (cart?.items || []).find((item) => item.key === key);
      const lineQty = Number(line?.quantity) || 0;
      if (cartItemTotal(cart) - lineQty + quantity > maxItems) {
        showGhaliaToast(`Bag limit is ${maxItems} items`);
        return;
      }
    }

    setCartLineBusy(key, true);
    optimisticCartLineQty(key, quantity);
    const optimistic = patchCartLineQuantity(window.GhaliaCart?.lastCart, key, quantity);
    applyOptimisticCartState(optimistic, { promoUi: false });

    let pending = cartLineMutations.get(key);
    if (!pending) {
      pending = { targetQty: quantity, timer: null, raf: null };
      cartLineMutations.set(key, pending);
    } else {
      pending.targetQty = quantity;
      clearCartLineMutationTimer(pending);
    }
    pending.raf = requestAnimationFrame(() => {
      pending.raf = null;
      commitCartLineChange(key, pending.targetQty);
    });
  };

  const removeCartLineNow = (key) => {
    clearCartLineMutationTimer(cartLineMutations.get(key));
    cartLineMutations.delete(key);
    setCartLineBusy(key, true);
    optimisticCartLineQty(key, 0);
    const optimistic = patchCartLineQuantity(window.GhaliaCart?.lastCart, key, 0);
    applyOptimisticCartState(optimistic, { promoUi: false });
    updateDrawerTitle(optimistic);
    commitCartLineChange(key, 0, { skipBusy: true });
  };

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn || btn.disabled) return;
    const cartLine = btn.closest('[data-cart-line-key]');
    if (cartLine) {
      e.preventDefault();
      e.stopPropagation();
      const input = cartLine.querySelector('[data-cart-line-qty]');
      if (!input || input.disabled) return;
      const key = cartLine.dataset.cartLineKey;
      const step = parseInt(btn.dataset.qtyStep, 10);
      const min = parseInt(input.min || '0', 10);
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(min, Math.min(max, current + step));
      if (next === current) return;
      queueCartLineChange(key, next);
      return;
    }
    const root = btn.closest('.qty');
    const input = root?.querySelector('input');
    if (!input) return;
    const step = parseInt(btn.dataset.qtyStep, 10);
    const value = Math.max(parseInt(input.min || '1', 10), (parseInt(input.value, 10) || 1) + step);
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  const normalizeHeaderCount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  };

  const getCartDisplayCount = (cart) => {
    if (!cart) return 0;
    if (Array.isArray(cart.items) && cart.items.length) {
      let sum = 0;
      cart.items.forEach((item) => {
        sum += Number(item.quantity) || 0;
      });
      if (sum > 0) return sum;
    }
    return normalizeHeaderCount(cart.item_count);
  };

  let cartBadgeSyncing = false;

  const scrubCartHeaderBtn = (count) => {
    if (cartBadgeSyncing) return;
    const btn = document.querySelector('[data-cart-header-btn]');
    if (!btn) return;
    const total = normalizeHeaderCount(count);
    cartBadgeSyncing = true;
    try {
      btn.querySelectorAll(':scope > *:not(svg.icon)').forEach((el) => {
        if (total === 0 || !el.hasAttribute('data-cart-count')) el.remove();
      });
      if (total === 0) btn.querySelectorAll('[data-cart-count], .site-actions__count').forEach((el) => el.remove());
    } finally {
      cartBadgeSyncing = false;
    }
  };

  /** Mount badge only when count ≥ 1; remove from DOM when 0 (Missoma-style). */
  const syncHeaderCountBadge = ({ parentSelector, badgeAttr, count }) => {
    const parent = document.querySelector(parentSelector);
    if (!parent) return;

    const total = normalizeHeaderCount(count);
    if (parent.matches('[data-cart-header-btn]')) scrubCartHeaderBtn(total);

    let badge = parent.querySelector(`[${badgeAttr}]`);

    if (total === 0) {
      badge?.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'site-actions__count';
      badge.setAttribute(badgeAttr, '');
      parent.appendChild(badge);
    }

    badge.textContent = String(total);
    badge.setAttribute('data-count', String(total));
    badge.removeAttribute('hidden');
    badge.setAttribute('aria-hidden', 'false');
  };

  const setActionCount = (selector, count) => {
    const total = normalizeHeaderCount(count);
    $$(selector).forEach((el) => {
      const parent = el.closest('[data-cart-header-btn], [data-wishlist-header-btn]');
      if (parent) {
        const attr = el.hasAttribute('data-cart-count') ? 'data-cart-count' : 'data-wishlist-count';
        const sel = parent.hasAttribute('data-cart-header-btn') ? '[data-cart-header-btn]' : '[data-wishlist-header-btn]';
        syncHeaderCountBadge({ parentSelector: sel, badgeAttr: attr, count: total });
        return;
      }
      if (total === 0) {
        el.remove();
        return;
      }
      el.textContent = String(total);
      el.setAttribute('data-count', String(total));
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
    });
  };

  const syncSwymWishlistCounter = (count) => {
    $$('.swym-wishlist-counter, .site-actions__btn--wishlist [class*="swym"][class*="count"]').forEach((el) => {
      const visible = count > 0;
      el.hidden = !visible;
      el.classList.toggle('is-empty', !visible);
      if (visible) {
        el.textContent = String(count);
        el.setAttribute('data-count', String(count));
      } else {
        el.textContent = '';
        el.removeAttribute('data-count');
      }
    });
  };

  const renderCart = (cart) => {
    syncHeaderCountBadge({
      parentSelector: '[data-cart-header-btn]',
      badgeAttr: 'data-cart-count',
      count: getCartDisplayCount(cart),
    });
    document.dispatchEvent(new CustomEvent('ghalia:cart:update', { detail: cart }));
  };

  const fetchCart = async () => {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  };

  const PROMO_STORAGE_KEY = 'ghalia_cart_promo_code';

  const promoCodeFromLineAllocations = (cart) => {
    for (const item of cart?.items || []) {
      for (const alloc of item.line_level_discount_allocations || []) {
        const title = alloc?.discount_application?.title;
        if (title) return String(title).trim();
      }
    }
    return '';
  };

  const cartPromoCode = (cart) => {
    const apps = cart?.cart_level_discount_applications || [];
    const codeApp = apps.find((d) => d?.type === 'discount_code') || apps[0];
    const fromCart = (codeApp?.title || '').trim();
    if (fromCart) return fromCart;
    return promoCodeFromLineAllocations(cart);
  };

  const cartHasPromoDiscount = (cart, drawer) => {
    if ((cart?.cart_level_discount_applications || []).length > 0) return true;
    if (Number(cart?.total_discount) > 0) return true;
    const pricing = window.GhaliaPricing;
    return (cart?.items || []).some((item) => {
      if (pricing?.isNativeBundleLine?.(item) || pricing?.isFreeGiftLine?.(item)) return false;
      if ((item.line_level_discount_allocations || []).length > 0) return true;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const domNet = Number(row?.dataset?.cartLineNet || 0);
      const finalNet = Number(item.final_line_price) || 0;
      return domNet > 0 && finalNet > 0 && finalNet < domNet;
    });
  };

  const storedPromoCode = () => {
    try {
      return sessionStorage.getItem(PROMO_STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  };

  const persistCartPromoCode = (cart) => {
    const code = cartPromoCode(cart);
    if (!code) return;
    try {
      sessionStorage.setItem(PROMO_STORAGE_KEY, code);
    } catch (_) { /* noop */ }
  };

  const clearStoredPromoCode = () => {
    try {
      sessionStorage.removeItem(PROMO_STORAGE_KEY);
    } catch (_) { /* noop */ }
  };

  const applyCartPromoCode = async (code) => {
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    });
    if (!res.ok) {
      let message = 'Could not update discount';
      try {
        const err = await res.json();
        message = err.description || err.message || message;
      } catch (_) { /* noop */ }
      throw new Error(message);
    }
    return res.json();
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const cartPromoMeetsMinimum = (cart) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || !window.GhaliaPromo?.meetsMinimum) return true;
    const qualifyingNet = window.GhaliaPricing?.qualifyingSubtotalNet?.(cart, drawer) || 0;
    return window.GhaliaPromo.meetsMinimum(qualifyingNet, drawer);
  };

  const cartHasShopifyPromo = (cart) => {
    if (cartPromoCode(cart)) return true;
    if (Number(cart?.total_discount) > 0) return true;
    return (cart?.items || []).some((item) => (item.line_level_discount_allocations || []).length > 0);
  };

  let promoSyncBusy = false;

  const lineHasPromoAllocation = (cart) =>
    (cart?.items || []).some((item) => (item.line_level_discount_allocations || []).length > 0);

  const syncCartPromoEligibility = async (cart, options = {}) => {
    if (!cart || promoSyncBusy) return cart;
    promoSyncBusy = true;
    try {
      const stored = storedPromoCode();
      const meets = cartPromoMeetsMinimum(cart);

      if (!meets && cartHasShopifyPromo(cart)) {
        try {
          return await applyCartPromoCode('');
        } catch (err) {
          console.warn('[GHALIA] Could not clear ineligible promo:', err);
          return cart;
        }
      }

      if (!meets || !stored) return cart;
      return ensureCartPromoCode(cart, options);
    } finally {
      promoSyncBusy = false;
    }
  };

  const defaultPromoCode = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    return window.GhaliaPromoConfig?.read?.(drawer)?.defaultCode || 'WELCOME';
  };

  const ensureCartPromoCode = async (cart, options = {}) => {
    const retries = Math.max(1, Number(options.retries) || 3);
    if (!cart) return cart;
    if (!cartPromoMeetsMinimum(cart)) return cart;
    persistCartPromoCode(cart);
    if (cartPromoCode(cart) || lineHasPromoAllocation(cart)) return cart;

    const stored = storedPromoCode() || defaultPromoCode();
    if (!stored) return cart;

    let latest = cart;
    for (let attempt = 0; attempt < retries; attempt += 1) {
      try {
        const updated = await applyCartPromoCode(stored);
        latest = updated;
        if (cartPromoCode(updated)) return updated;
      } catch (err) {
        if (attempt === retries - 1) {
          console.warn('[GHALIA] Could not restore promo code:', err);
        }
      }
      if (attempt < retries - 1) {
        await sleep(250 * (attempt + 1));
        try {
          latest = await fetchCart();
        } catch (_) { /* noop */ }
      }
    }
    return latest;
  };

  const syncCartPromoUI = (cart) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const promo = drawer.querySelector('[data-cart-promo]');
    const form = drawer.querySelector('[data-promo-form]');
    const applied = drawer.querySelector('[data-cart-promo-applied]');
    const chip = drawer.querySelector('[data-cart-promo-chip]');
    const input = drawer.querySelector('#cart-promo-code');
    const stored = storedPromoCode();
    const hasActivePromo = cartHasPromoDiscount(cart, drawer);
    const displayCode =
      cartPromoCode(cart) ||
      stored ||
      (promo?.dataset?.appliedCode || '').trim();
    if (input) input.value = displayCode;
    if (hasActivePromo || stored) {
      const chipText = (
        displayCode ||
        promoCodeFromLineAllocations(cart) ||
        stored ||
        'DISCOUNT'
      ).toUpperCase();
      promo?.classList.add('is-applied');
      if (form) {
        form.hidden = true;
        form.setAttribute('hidden', '');
      }
      if (applied) {
        applied.hidden = false;
        applied.removeAttribute('hidden');
      }
      if (chip) chip.textContent = chipText;
    } else {
      promo?.classList.remove('is-applied');
      if (form) {
        form.hidden = false;
        form.removeAttribute('hidden');
      }
      if (applied) {
        applied.hidden = true;
        applied.setAttribute('hidden', '');
      }
      if (chip) chip.textContent = '';
    }
  };

  const FREE_GIFT_PROP = '_ghalia_free_gift';
  let freeGiftSyncing = false;

  const getCartGiftConfig = () => {
    if (!cartDrawer) return null;
    const variantId = cartDrawer.dataset.cartGiftVariantId;
    const threshold = parseInt(cartDrawer.dataset.cartGiftThreshold, 10);
    const productId = cartDrawer.dataset.cartGiftProductId;
    if (!variantId || !Number.isFinite(threshold)) return null;
    return {
      variantId: String(variantId),
      threshold: threshold < 0 ? 0 : threshold,
      productId: productId ? String(productId) : '',
      alwaysGift: threshold <= 0,
    };
  };

  const isFreeGiftLine = (item, config) => {
    if (!item || !config) return false;
    if (String(item.variant_id) === config.variantId) return true;
    if (config.productId && String(item.product_id) === config.productId) return true;
    const props = item.properties || {};
    return props[FREE_GIFT_PROP] === 'true';
  };

  const getQualifyingCartTotal = (cart, config) =>
    (cart?.items || []).reduce((sum, item) => {
      if (isFreeGiftLine(item, config)) return sum;
      return sum + (Number(item.final_line_price ?? item.line_price) || 0);
    }, 0);

  const findFreeGiftLine = (cart, config) =>
    (cart?.items || []).find((item) => isFreeGiftLine(item, config));

  const syncCartFreeGift = async (cart) => {
    if (freeGiftSyncing || !cart) return cart;
    const config = getCartGiftConfig();
    if (!config) return cart;

    const qualifying = getQualifyingCartTotal(cart, config);
    const giftLine = findFreeGiftLine(cart, config);
    const hasPaidLines = (cart?.items || []).some((item) => !isFreeGiftLine(item, config));
    const shouldHaveGift = config.alwaysGift ? hasPaidLines : qualifying >= config.threshold;
    const root = window.Shopify?.routes?.root || '/';

    try {
      freeGiftSyncing = true;
      if (shouldHaveGift && !giftLine) {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: config.variantId, quantity: 1, properties: { [FREE_GIFT_PROP]: 'true' } }],
          }),
        });
        if (!res.ok) throw new Error('Could not add free gift');
        return fetchCart();
      }
      if (!shouldHaveGift && giftLine) {
        const res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: giftLine.key, quantity: 0 }),
        });
        if (!res.ok) throw new Error('Could not remove free gift');
        return res.json();
      }
    } catch (err) {
      console.error(err);
    } finally {
      freeGiftSyncing = false;
    }
    return cart;
  };

  const PREVIEW_THEME_STORAGE_KEY = 'ghalia_preview_theme_id';

  const shopPreviewHosts = () => {
    const hosts = new Set([window.location.hostname]);
    const body = document.body;
    const permanent = body?.dataset?.shopPermanentDomain;
    const primary = body?.dataset?.shopPrimaryHost;
    if (permanent) hosts.add(permanent);
    if (primary) hosts.add(primary);
    if (window.Shopify?.shop) hosts.add(window.Shopify.shop);
    return hosts;
  };

  const isShopPreviewHost = (hostname) => {
    if (!hostname) return false;
    const hosts = shopPreviewHosts();
    if (hosts.has(hostname)) return true;
    return hostname.endsWith('.myshopify.com');
  };

  const persistPreviewThemeId = (previewId) => {
    if (!previewId) return;
    try {
      sessionStorage.setItem(PREVIEW_THEME_STORAGE_KEY, previewId);
    } catch (_) { /* noop */ }
  };

  const previewThemeId = () => {
    const fromQuery = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (fromQuery) {
      persistPreviewThemeId(fromQuery);
      return fromQuery;
    }
    try {
      const stored = sessionStorage.getItem(PREVIEW_THEME_STORAGE_KEY);
      if (stored) return stored;
    } catch (_) { /* noop */ }
    const fromTheme =
      window.GHALIA_PREVIEW_THEME_ID ||
      document.body?.dataset?.previewThemeId ||
      null;
    if (fromTheme) persistPreviewThemeId(fromTheme);
    return fromTheme;
  };

  const bindPreviewThemePersistence = () => {
    const url = new URL(window.location.href);
    let previewId = previewThemeId();

    if (!previewId && document.referrer) {
      try {
        const ref = new URL(document.referrer);
        const fromRef = ref.searchParams.get('preview_theme_id');
        if (fromRef && isShopPreviewHost(ref.hostname) && isShopPreviewHost(url.hostname)) {
          previewId = fromRef;
          persistPreviewThemeId(fromRef);
        }
      } catch (_) { /* noop */ }
    }

    if (!previewId) return;

    if (!url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', previewId);
      window.history.replaceState({}, '', url.toString());
    }

    document.addEventListener('click', (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      let link;
      try {
        link = new URL(anchor.href, window.location.origin);
      } catch (_) {
        return;
      }
      if (!isShopPreviewHost(link.hostname)) return;
      if (link.searchParams.has('preview_theme_id')) return;
      link.searchParams.set('preview_theme_id', previewId);
      anchor.href = link.toString();
    });
  };

  const fetchCartDrawer = async () => {
    if (!cartDrawer) return;
    const url = new URL(`${window.Shopify?.routes?.root || '/'}`, window.location.origin);
    url.searchParams.set('sections', 'cart-drawer');
    const previewId = previewThemeId();
    if (previewId) url.searchParams.set('preview_theme_id', previewId);
    const res = await fetch(url.toString());
    const data = await res.json();
    const tmp = document.createElement('div');
    tmp.innerHTML = data['cart-drawer'];
    const freshDrawer = tmp.querySelector('[data-cart-drawer]');
    const fresh = freshDrawer?.querySelector('[data-cart-drawer-body]') || tmp.querySelector('[data-cart-drawer-body]');
    const target = cartDrawer.querySelector('[data-cart-drawer-body]');
    if (fresh && target) target.replaceWith(fresh);

    if (freshDrawer) {
      cartDrawer.dataset.cartGiftVariantId = freshDrawer.dataset.cartGiftVariantId || '';
      cartDrawer.dataset.cartGiftThreshold = freshDrawer.dataset.cartGiftThreshold || '';
      cartDrawer.dataset.cartGiftProductId = freshDrawer.dataset.cartGiftProductId || '';
    }

    const freshTitle = freshDrawer?.querySelector('.drawer__title');
    const targetTitle = cartDrawer.querySelector('.drawer__title');
    if (freshTitle && targetTitle) targetTitle.textContent = freshTitle.textContent;

    const freshFooter = freshDrawer?.querySelector('[data-cart-drawer-footer], .drawer__footer');
    const targetFooter = cartDrawer.querySelector('[data-cart-drawer-footer], .drawer__footer');
    if (freshFooter) {
      if (targetFooter) targetFooter.replaceWith(freshFooter);
      else cartDrawer.appendChild(freshFooter);
    }

    if (typeof initCartUpsellDrawer === 'function') initCartUpsellDrawer(cartDrawer);
    hydrateCartWrapAddForm();
    document.dispatchEvent(new CustomEvent('ghalia:cart-drawer:refreshed'));
  };

  const processCartUpdate = async (cart, options = {}) => {
    const refresh = options.refresh || (options.fast ? 'light' : 'full');
    const giftConfig = getCartGiftConfig();
    const hadGift = giftConfig ? Boolean(findFreeGiftLine(cart, giftConfig)) : false;
    let synced = await syncCartFreeGift(cart);
    const hasGift = giftConfig ? Boolean(findFreeGiftLine(synced, giftConfig)) : false;
    const giftMutated = hadGift !== hasGift;
    const needsDrawer = refresh === 'full' || giftMutated || needsFullDrawerRefresh(cart, synced);
    if (needsDrawer) {
      suppressDrawerRefreshHandler = true;
      try {
        await fetchCartDrawer();
      } finally {
        suppressDrawerRefreshHandler = false;
      }
    } else {
      updateDrawerTitle(synced);
      updateGiftBanner(synced);
      pruneRemovedCartLines(synced);
    }
    applyCartLocally(synced);
    if (options.syncPromo) {
      syncCartPromoEligibility(synced, { retries: 1 })
        .then((promoCart) => {
          if (!promoCart || promoCart === synced) return;
          applyCartLineChangeFast(cart, promoCart);
        })
        .catch(() => {});
    }
    if (
      refresh === 'full' &&
      (cartHasExtraWrap(synced) ||
        document.querySelector('[data-cart-gift-note]')?.value.trim() ||
        document.querySelector('[data-cart-wrap-note]')?.value.trim())
    ) {
      await saveCartOrderNote(synced);
    }
    return synced;
  };

  const addVariantToCart = async (variantId, trigger, options = {}) => {
    const { quantity = 1, openCart = true, skipStockCheck = false } = options;
    if (!variantId) throw new Error('Missing variant');
    const maxItems = getCartMaxItems();
    const beforeCart = window.GhaliaCart?.lastCart;
    if (maxItems > 0 && cartItemTotal(beforeCart) + quantity > maxItems) {
      showGhaliaToast(`Bag limit is ${maxItems} items`);
      throw new Error(`Bag limit is ${maxItems} items`);
    }
    const existingLine = (beforeCart?.items || []).find(
      (item) => String(item.variant_id) === String(variantId)
    );
    trigger?.setAttribute('aria-busy', 'true');
    try {
      if (openCart && cartDrawer) openDrawer(cartDrawer);
      if (existingLine) {
        const optimistic = patchCartLineQuantity(beforeCart, existingLine.key, existingLine.quantity + quantity);
        applyOptimisticCartState(optimistic, { promoUi: false });
      } else if (beforeCart?.items?.length) {
        renderCart({
          ...beforeCart,
          item_count: cartItemTotal(beforeCart) + quantity,
        });
      }
      if (!skipStockCheck && trigger?.dataset?.variantAvailable !== 'true') {
        await assertVariantCanAdd(variantId, quantity);
      }
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      if (!res.ok) {
        let message = 'Could not add to bag';
        try {
          const err = await res.json();
          message = err.description || err.message || message;
        } catch (_) { /* noop */ }
        throw new Error(message);
      }
      variantStockCache.clear();
      const cart = await fetchCart();
      const light = Boolean(existingLine) && !needsFullDrawerRefresh(beforeCart, cart);
      const synced = await processCartUpdate(cart, {
        refresh: light ? 'light' : 'full',
        syncPromo: true,
      });
      if (openCart && cartDrawer) openDrawer(cartDrawer);
      document.dispatchEvent(
        new CustomEvent('ghalia:cart:add-variant:done', { detail: { variantId, quantity, cart: synced } })
      );
      return synced;
    } catch (err) {
      showGhaliaToast(err.message || 'Could not add to bag');
      document.dispatchEvent(
        new CustomEvent('ghalia:cart:add-variant:error', { detail: { variantId, error: err } })
      );
      throw err;
    } finally {
      trigger?.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('ghalia:cart:add-variant', async (e) => {
    const { variantId, trigger, quantity = 1, openCart = true } = e.detail || {};
    if (!variantId) return;
    try {
      await addVariantToCart(variantId, trigger, { quantity, openCart });
    } catch (err) {
      console.error(err);
    }
  });

  const addItemsToCart = async (items, trigger, options = {}) => {
    const { openCart = true } = options;
    const payload = (items || []).filter((item) => item?.id).map((item) => {
      const row = {
        id: Number(item.id),
        quantity: Number(item.quantity || 1),
      };
      if (item.properties && Object.keys(item.properties).length) {
        row.properties = item.properties;
      }
      return row;
    });
    if (!payload.length) throw new Error('Missing variant');
    trigger?.setAttribute('aria-busy', 'true');
    try {
      for (const row of payload) {
        await assertVariantCanAdd(row.id, row.quantity);
      }
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: payload }),
      });
      if (!res.ok) {
        let message = 'Could not add to bag';
        try {
          const err = await res.json();
          message = err.description || err.message || message;
        } catch (_) { /* noop */ }
        throw new Error(message);
      }
      variantStockCache.clear();
      const cart = await fetchCart();
      const synced = await processCartUpdate(cart);
      if (openCart && cartDrawer) openDrawer(cartDrawer);
      document.dispatchEvent(
        new CustomEvent('ghalia:cart:add-items:done', { detail: { items: payload, cart: synced } })
      );
      return synced;
    } catch (err) {
      showGhaliaToast(err.message || 'Could not add to bag');
      document.dispatchEvent(
        new CustomEvent('ghalia:cart:add-items:error', { detail: { items: payload, error: err } })
      );
      throw err;
    } finally {
      trigger?.removeAttribute('aria-busy');
    }
  };

  window.GhaliaCart = {
    addVariant: (variantId, opts = {}) => addVariantToCart(variantId, null, opts),
    addItems: addItemsToCart,
    lastCart: null,
    refresh: async () => processCartUpdate(await fetchCart()),
  };

  /* Cart gift sections — sync gift message + extra wrap to order notes */
  let giftNoteTimer = null;
  let giftNoteSaving = false;

  const getCartWrapVariantId = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const el =
      drawer?.querySelector('[data-cart-wrap-variant-id]') ||
      drawer?.querySelector('[data-cart-wrap-variant-input]') ||
      document.querySelector('[data-cart-wrap-variant-id]');
    return parseInt(el?.dataset?.cartWrapVariantId || el?.value || '', 10) || 0;
  };

  const hydrateCartWrapAddForm = async () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const form = drawer.querySelector('[data-cart-wrap-add-form]');
    const input = form?.querySelector('[data-cart-wrap-variant-input]');
    if (input?.value) return;
    const handle = drawer.dataset.cartWrapHandle;
    const sku = drawer.dataset.cartWrapSku || '';
    if (!handle) return;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const product = await res.json();
      const variant =
        (product?.variants || []).find((v) => sku && v.sku === sku) ||
        (product?.variants || []).find((v) => v.available) ||
        product?.variants?.[0];
      if (!variant?.id) return;
      if (!form) {
        const panel = drawer.querySelector('.cart-wrap--extra .cart-wrap__form');
        if (!panel) return;
        const wrapForm = document.createElement('form');
        wrapForm.className = 'cart-wrap__add-form';
        wrapForm.setAttribute('action', `${window.Shopify?.routes?.root || '/'}cart/add`);
        wrapForm.setAttribute('method', 'post');
        wrapForm.dataset.cartWrapAddForm = '';
        wrapForm.innerHTML = `<input type="hidden" name="id" value="${variant.id}" data-cart-wrap-variant-input><button class="btn btn--primary btn--block cart-wrap__add" type="submit" data-cart-wrap-add-btn>Add to Bag</button>`;
        panel.appendChild(wrapForm);
      } else if (input) {
        input.value = String(variant.id);
      }
      drawer.dataset.cartWrapVariantId = String(variant.id);
    } catch (_) { /* noop */ }
  };

  const cartHasExtraWrap = (cart) => {
    const wrapId = getCartWrapVariantId();
    if (!wrapId || !cart?.items?.length) return false;
    return cart.items.some((item) => item.variant_id === wrapId);
  };

  const getOrderNoteLabels = () => {
    const root = document.querySelector('[data-cart-order-notes]');
    const fallback = {
      gift: 'FREE — Gift card message (print on card):',
      wrap: 'PAID ADD-ON — Extra gift wrap note:',
      wrapAdded: 'Extra wrap product in cart',
    };
    if (!root) return fallback;
    return {
      gift: root.dataset.orderNoteGiftPrefix || fallback.gift,
      wrap: root.dataset.orderNoteWrapPrefix || fallback.wrap,
      wrapAdded: root.dataset.orderNoteWrapAdded || fallback.wrapAdded,
    };
  };

  const buildCartOrderNote = (giftMessage, wrapMessage, hasExtraWrap, labels) => {
    const parts = [];
    if (giftMessage) parts.push(`${labels.gift}\n${giftMessage}`);
    const wrapLines = [];
    if (hasExtraWrap) wrapLines.push(labels.wrapAdded);
    if (wrapMessage) wrapLines.push(wrapMessage);
    if (wrapLines.length) parts.push(`${labels.wrap}\n${wrapLines.join('\n')}`);
    return parts.join('\n\n');
  };

  const updateGiftNoteCount = (textarea) => {
    const isWrap = textarea.matches('[data-cart-wrap-note]');
    const countSel = isWrap ? '[data-cart-wrap-note-count]' : '[data-cart-gift-note-count]';
    const countEl = textarea.closest('.cart-wrap__panel')?.querySelector(countSel);
    if (!countEl) return;
    const max = parseInt(textarea.dataset.cartGiftNoteMax || '100', 10);
    const remaining = Math.max(0, max - textarea.value.length);
    const template = countEl.dataset.countTemplate || '';
    countEl.textContent = template.replace(/__COUNT__/g, String(remaining));
  };

  const saveCartOrderNote = async (cart) => {
    if (giftNoteSaving) return;
    const labels = getOrderNoteLabels();
    const giftMessage = document.querySelector('[data-cart-gift-note]')?.value.trim() || '';
    const wrapMessage = document.querySelector('[data-cart-wrap-note]')?.value.trim() || '';
    let activeCart = cart;
    if (!activeCart) {
      try {
        activeCart = await fetchCart();
      } catch (err) {
        console.error(err);
        return;
      }
    }
    const note = buildCartOrderNote(giftMessage, wrapMessage, cartHasExtraWrap(activeCart), labels);
    giftNoteSaving = true;
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    } catch (err) {
      console.error(err);
    } finally {
      giftNoteSaving = false;
    }
  };

  document.addEventListener('input', (e) => {
    const textarea = e.target.closest('[data-cart-gift-note], [data-cart-wrap-note]');
    if (!textarea) return;
    updateGiftNoteCount(textarea);
    clearTimeout(giftNoteTimer);
    giftNoteTimer = window.setTimeout(() => saveCartOrderNote(), 500);
  });

  document.addEventListener(
    'blur',
    (e) => {
      const textarea = e.target.closest('[data-cart-gift-note], [data-cart-wrap-note]');
      if (!textarea) return;
      clearTimeout(giftNoteTimer);
      saveCartOrderNote();
    },
    true
  );

  const updateCartDiscount = async (code, trigger) => {
    trigger?.setAttribute('aria-busy', 'true');
    try {
      if (code) {
        try {
          sessionStorage.setItem(PROMO_STORAGE_KEY, code);
        } catch (_) { /* noop */ }
      } else {
        clearStoredPromoCode();
      }
      const cart = await applyCartPromoCode(code);
      await processCartUpdate(cart, { syncPromo: true });
      if (cartDrawer) openDrawer(cartDrawer);
    } catch (err) {
      console.error(err);
    } finally {
      trigger?.removeAttribute('aria-busy');
    }
  };

  let promoBgSyncTimer = null;

  const scheduleBackgroundPromoSync = (cart) => {
    if (!cart || !storedPromoCode() || !cartPromoMeetsMinimum(cart)) return;
    if (cartHasShopifyPromo(cart)) return;
    clearTimeout(promoBgSyncTimer);
    promoBgSyncTimer = window.setTimeout(() => {
      if (promoSyncBusy) return;
      syncCartPromoEligibility(cart, { retries: 1 })
        .then((synced) => {
          if (!synced || synced === window.GhaliaCart?.lastCart) return;
          if (window.GhaliaCart) window.GhaliaCart.lastCart = synced;
          refreshCartSummaryLight(synced);
          syncCartPromoUI(synced);
        })
        .catch(() => {});
    }, 1200);
  };

  document.addEventListener('ghalia:cart:stack-discount', (e) => {
    const cart = e.detail?.cart || window.GhaliaCart?.lastCart;
    syncCartPromoUI(cart);
    scheduleBackgroundPromoSync(cart);
  });

  document.addEventListener('ghalia:cart-drawer:refreshed', () => {
    if (suppressDrawerRefreshHandler) {
      syncCartCheckoutState();
      return;
    }
    syncCartPromoUI(window.GhaliaCart?.lastCart);
    syncCartCheckoutState();
  });

  document.addEventListener('ghalia:drawer:open', (e) => {
    const drawer = e.detail?.drawer;
    if (!drawer?.hasAttribute('data-cart-drawer')) return;
    window.GhaliaCartQuickAdd?.close?.();
    drawer.classList.remove('is-quick-add-open');
    syncCartPromoUI(window.GhaliaCart?.lastCart);
  });

  document.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-promo-remove]');
    if (!removeBtn) return;
    e.preventDefault();
    await updateCartDiscount('', removeBtn);
  });

  document.addEventListener('submit', async (e) => {
    const promoForm = e.target.closest('[data-promo-form]');
    if (!promoForm) return;
    e.preventDefault();
    const input = promoForm.querySelector('[name="discount"]');
    const code = input?.value.trim();
    if (!code) {
      input?.focus();
      return;
    }
    const btn = promoForm.querySelector('[type="submit"]');
    await updateCartDiscount(code, btn);
  });

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    btn?.setAttribute('aria-busy', 'true');
    const fd = new FormData(form);
    const variantId = fd.get('id');
    const qty = parseInt(fd.get('quantity') || '1', 10) || 1;
    fd.append('sections', 'cart-drawer');
    try {
      if (variantId) await assertVariantCanAdd(variantId, qty);
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/javascript' }
      });
      if (!res.ok) throw new Error(await res.text());
      variantStockCache.clear();
      const cart = await fetchCart();
      const synced = await processCartUpdate(cart, { refresh: 'full', syncPromo: true });
      if (form.classList.contains('cart-wrap__add-form')) {
        await saveCartOrderNote(synced);
      }
      openDrawer(cartDrawer);
    } catch (err) {
      console.error(err);
      showGhaliaToast(err.message || 'Could not add to bag');
      form.dispatchEvent(new CustomEvent('ghalia:cart:error', { detail: err }));
    } finally {
      btn?.removeAttribute('aria-busy');
    }
  });

  window.GhaliaCartQuickAddDeps = { addVariantToCart, openDrawer, cartDrawer };
  window.GhaliaCartQuickAdd?.init(window.GhaliaCartQuickAddDeps);

  /* ---------- Wishlist (Swym sync + local UI) ---------- */
  const WISHLIST_KEY = 'ghalia-wishlist';
  const wishlistStrings = window.Shopify?.strings || {};

  const wishlistItemKey = (productId, variantId) => `${productId}:${variantId || ''}`;

  const resolveWishlistHandle = (item) => {
    if (item?.productHandle) return String(item.productHandle).trim();
    const url = item?.productUrl || '';
    if (!url) return '';
    try {
      const path = url.startsWith('http') ? new URL(url, window.location.origin).pathname : url;
      const match = path.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  };

  const isValidWishlistItem = (item) => {
    const productId = String(item?.productId || '').trim();
    return !!productId && !!resolveWishlistHandle(item);
  };

  const normalizeWishlistItems = (items) => {
    const seen = new Set();
    const out = [];
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (!isValidWishlistItem(item)) return;
      const productId = String(item.productId).trim();
      const variantId = String(item?.variantId || '');
      const key = wishlistItemKey(productId, variantId);
      if (seen.has(key)) return;
      seen.add(key);
      const handle = resolveWishlistHandle(item);
      out.push({
        ...item,
        productId,
        variantId,
        productHandle: item.productHandle || handle
      });
    });
    return out;
  };

  const getWishlistBadgeCount = (items) => {
    const productIds = new Set();
    normalizeWishlistItems(items).forEach((item) => productIds.add(String(item.productId)));
    return productIds.size;
  };

  const readLocalWishlist = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      const normalized = normalizeWishlistItems(raw);
      if (normalized.length !== raw.length) {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(normalized));
        document.dispatchEvent(new CustomEvent('ghalia:wishlist:update'));
      }
      return normalized;
    } catch (_) {
      localStorage.removeItem(WISHLIST_KEY);
      return [];
    }
  };

  const writeLocalWishlist = (items) => {
    const normalized = normalizeWishlistItems(items);
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(normalized));
    updateWishlistCount();
    document.dispatchEvent(new CustomEvent('ghalia:wishlist:update'));
  };

  const showWishlistToast = (message) => showGhaliaToast(message);

  let wishlistBadgeSyncing = false;

  const scrubWishlistHeaderBtn = (count) => {
    const btn = document.querySelector('[data-wishlist-header-btn]');
    if (!btn) return;
    const total = normalizeHeaderCount(count);
    btn.querySelectorAll(':scope > *:not(svg.icon)').forEach((el) => {
      if (total === 0 || !el.hasAttribute('data-wishlist-count')) el.remove();
    });
    if (total === 0) btn.querySelectorAll('[data-wishlist-count], .site-actions__count').forEach((el) => el.remove());
  };

  const updateWishlistCount = () => {
    if (wishlistBadgeSyncing) return;
    wishlistBadgeSyncing = true;
    const items = readLocalWishlist();
    const count = getWishlistBadgeCount(items);
    try {
      scrubWishlistHeaderBtn(count);
      syncHeaderCountBadge({
        parentSelector: '[data-wishlist-header-btn]',
        badgeAttr: 'data-wishlist-count',
        count,
      });
      syncSwymWishlistCounter(0);
    } finally {
      wishlistBadgeSyncing = false;
    }
  };

  const setWishlistBtnState = (btn, active) => {
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    const savedLabel = btn.dataset.wishlistLabelSaved || '';
    const addLabel = btn.dataset.wishlistLabelAdd || btn.getAttribute('aria-label') || '';
    if (savedLabel) btn.setAttribute('aria-label', active ? savedLabel : addLabel);
  };

  const syncWishlistButtons = (updateHeader = true) => {
    const local = new Set(readLocalWishlist().map((item) => wishlistItemKey(item.productId, item.variantId)));
    $$('[data-wishlist-toggle]').forEach((btn) => {
      const productId = btn.getAttribute('data-wishlist-toggle') || '';
      const variantId = btn.getAttribute('data-variant-id') || '';
      const active = local.has(wishlistItemKey(productId, variantId))
        || [...local].some((key) => key.split(':')[0] === productId);
      setWishlistBtnState(btn, active);
    });
    if (updateHeader) updateWishlistCount();
  };

  const swymPayloadFor = ({ productId, variantId, productUrl }) => ({
    empi: parseInt(productId, 10),
    epi: parseInt(variantId, 10),
    du: productUrl
  });

  const syncToSwym = (payload, action) => {
    const swymPayload = swymPayloadFor(payload);
    const run = (swat) => {
      if (action === 'add' && swat?.addToWishList) {
        swat.addToWishList(swymPayload, () => {}, () => {});
      } else if (action === 'remove' && swat?.removeFromWishList) {
        swat.removeFromWishList(swymPayload, () => {}, () => {});
      }
    };
    if (window._swat) run(window._swat);
    else {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(run);
    }
  };

  const addToWishlist = (payload, btn) => {
    const { productId, variantId } = payload;
    const items = readLocalWishlist();
    const key = wishlistItemKey(productId, variantId);
    if (!items.some((item) => wishlistItemKey(item.productId, item.variantId) === key)) {
      items.push(payload);
      writeLocalWishlist(items);
    }
    if (btn) setWishlistBtnState(btn, true);
    showWishlistToast(wishlistStrings.wishlistAdded || 'Saved to wishlist');
    syncToSwym(payload, 'add');
    document.dispatchEvent(new CustomEvent('ghalia:wishlist:add', { detail: payload }));
  };

  const removeFromWishlist = (payload, btn) => {
    const { productId, variantId } = payload;
    const items = readLocalWishlist().filter((item) => wishlistItemKey(item.productId, item.variantId) !== wishlistItemKey(productId, variantId));
    writeLocalWishlist(items);
    if (btn) setWishlistBtnState(btn, false);
    showWishlistToast(wishlistStrings.wishlistRemoved || 'Removed from wishlist');
    syncToSwym(payload, 'remove');
    document.dispatchEvent(new CustomEvent('ghalia:wishlist:remove', { detail: payload }));
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const productId = btn.getAttribute('data-wishlist-toggle') || '';
    const variantId = btn.getAttribute('data-variant-id') || btn.closest('[data-product-card]')?.querySelector('[data-quick-add]')?.getAttribute('data-quick-add') || '';
    const productUrl = btn.getAttribute('data-product-url') || btn.closest('[data-product-card]')?.querySelector('[data-product-url]')?.getAttribute('href') || window.location.pathname;
    const productHandle = btn.getAttribute('data-product-handle') || btn.closest('[data-product-card]')?.querySelector('[data-wishlist-toggle]')?.getAttribute('data-product-handle') || '';
    if (!productId) return;

    const payload = { productId, variantId, productUrl, productHandle };

    if (btn.classList.contains('is-active')) {
      removeFromWishlist(payload, btn);
      return;
    }

    addToWishlist(payload, btn);
  });

  syncWishlistButtons();
  document.addEventListener('ghalia:wishlist:sync-buttons', (e) => {
    const updateHeader = e.detail?.header !== false;
    syncWishlistButtons(updateHeader);
  });

  const hideInjectedSwymCounters = () => {
    document
      .querySelectorAll(
        '.swym-wishlist-counter, [class*="swym"][class*="count"], [data-wishlist-header-btn] [class*="swym"]'
      )
      .forEach((node) => {
        if (node.matches('[data-wishlist-count]')) return;
        node.hidden = true;
        node.style.display = 'none';
        node.setAttribute('aria-hidden', 'true');
      });
  };

  /* One-shot Swym cleanup — no MutationObservers (they froze the site) */
  ensureWishlistHeaderOpensDrawer();
  hideInjectedSwymCounters();
  updateWishlistCount();
  window.setTimeout(() => {
    ensureWishlistHeaderOpensDrawer();
    hideInjectedSwymCounters();
    updateWishlistCount();
  }, 1500);
  window.setTimeout(() => {
    ensureWishlistHeaderOpensDrawer();
    hideInjectedSwymCounters();
    updateWishlistCount();
  }, 4000);

  if (document.body.classList.contains('template-page-wishlist')
    || document.body.classList.contains('template-page-swym')
    || document.body.classList.contains('template-page-swym-wishlist')) {
    window.addEventListener('load', () => window.setTimeout(openWishlistDrawer, 150));
  }

  window.GhaliaWishlist = {
    read: readLocalWishlist,
    write: writeLocalWishlist,
    getCount: () => getWishlistBadgeCount(readLocalWishlist()),
    remove: (payload) => removeFromWishlist(payload, null)
  };

  fetchCart()
    .then((cart) => {
      if (window.GhaliaCart) window.GhaliaCart.lastCart = cart;
      renderCart(cart);
      scheduleCartPricing(cart);
      window.setTimeout(() => {
        processCartUpdate(cart, { fast: true, syncPromo: true }).catch(() => {});
      }, 400);
    })
    .catch(() => renderCart({ item_count: 0, items: [] }));

  /* Change line item quantity / remove */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-line-qty]');
    if (!input || input.disabled) return;
    e.stopPropagation();
    const key = input.dataset.cartLineQty;
    const quantity = parseInt(input.value, 10) || 0;
    queueCartLineChange(key, quantity);
  });
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-cart-line-remove]');
    if (!rm || rm.disabled) return;
    e.preventDefault();
    e.stopPropagation();
    removeCartLineNow(rm.dataset.cartLineRemove);
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form');
    const checkoutBtn = form?.querySelector('button[name="checkout"]');
    if (!checkoutBtn) return;
    if (document.querySelector('[data-cart-line-available="false"]')) {
      e.preventDefault();
      showGhaliaToast(window.Shopify?.strings?.cart?.checkoutBlockedOos || 'Remove sold-out items before checkout');
      syncCartCheckoutState();
      return;
    }
    if (!form.closest('[data-cart-drawer]')) return;
    e.preventDefault();
    const root = window.Shopify?.routes?.root || '/';
    if (storedPromoCode()) {
      syncCartPromoEligibility(window.GhaliaCart?.lastCart, { retries: 0 }).catch(() => {});
    }
    window.location.assign(`${root}checkout`);
  });

  document.addEventListener('ghalia:cart:error', (e) => {
    const err = e.detail;
    const message = err?.message || (typeof err === 'string' ? err : '');
    if (message) showGhaliaToast(message);
  });

  /* ---------- Marquee duplication (for seamless loop) ---------- */
  $$('[data-marquee]').forEach((m) => {
    const track = m.querySelector('.marquee__track');
    if (!track) return;
    track.innerHTML = track.innerHTML + track.innerHTML;
  });

  /* ---------- Lazy reveal ---------- */
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.1 });
    $$('[data-reveal]').forEach((el) => io.observe(el));
  }

  /* ---------- Footer columns — static desktop, accordion mobile ---------- */
  const footerColMq = window.matchMedia('(max-width: 1024px)');

  const syncFooterCols = () => {
    $$('[data-footer-col]').forEach((col) => {
      const head = col.querySelector('.site-footer__col-head');
      const isMobile = footerColMq.matches;
      if (!isMobile) {
        col.classList.remove('is-open');
        head?.setAttribute('aria-expanded', 'true');
        return;
      }
      const open = col.classList.contains('is-open');
      head?.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  };

  $$('[data-footer-col] .site-footer__col-head').forEach((head) => {
    head.addEventListener('click', () => {
      if (!footerColMq.matches) return;
      const col = head.closest('[data-footer-col]');
      if (!col) return;
      const willOpen = !col.classList.contains('is-open');
      $$('[data-footer-col]').forEach((item) => item.classList.remove('is-open'));
      if (willOpen) col.classList.add('is-open');
      syncFooterCols();
    });
  });

  footerColMq.addEventListener('change', syncFooterCols);
  syncFooterCols();

  $$('details.pdp__accordion, details.pdp-accordion, details.faq-accordion, details.facet, details.ts-facet').forEach((d) => {
    d.setAttribute('aria-expanded', d.open ? 'true' : 'false');
    d.addEventListener('toggle', () => d.setAttribute('aria-expanded', d.open ? 'true' : 'false'));
  });

  /* ---------- Product card image swipe (touch + drag) ---------- */
  const boundMediaTracks = new WeakSet();
  const preloadedSiblingImages = new Set();

  const releaseMediaTrack = (track) => {
    if (!track) return;
    const state = track._mediaDrag;
    if (state?.pointerId != null) {
      try { track.releasePointerCapture(state.pointerId); } catch (_) { /* noop */ }
    }
    track._mediaDrag = { dragging: false, pointerId: null, moved: false };
    track.classList.remove('is-dragging');
  };

  const initProductMediaTrack = (track) => {
    if (!track || boundMediaTracks.has(track)) return;
    boundMediaTracks.add(track);
    track._mediaDrag = { dragging: false, pointerId: null, moved: false };

    const link = track.closest('.product-card__media-link');

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('.product-card__swatch, [data-sibling-swatch]')) return;
      e.stopPropagation();
      releaseMediaTrack(track);
      track._mediaDrag.dragging = true;
      track._mediaDrag.moved = false;
      track._mediaDrag.pointerId = e.pointerId;
      track._mediaDrag.startX = e.clientX;
      track._mediaDrag.scrollStart = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(e.pointerId);
    });

    track.addEventListener('pointermove', (e) => {
      const state = track._mediaDrag;
      if (!state?.dragging) return;
      const dx = e.clientX - state.startX;
      if (Math.abs(dx) > 4) state.moved = true;
      track.scrollLeft = state.scrollStart - dx;
    });

    const endDrag = (e) => {
      const state = track._mediaDrag;
      if (!state?.dragging) return;
      if (e?.pointerId != null && track.hasPointerCapture?.(e.pointerId)) {
        try { track.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
      }
      state.dragging = false;
      state.pointerId = null;
      track.classList.remove('is-dragging');
      if (state.moved) setTimeout(() => { state.moved = false; }, 0);
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    if (link) {
      link.addEventListener('click', (e) => {
        if (track._mediaDrag?.moved) e.preventDefault();
      });
    }
  };

  const parseSiblingImages = (swatch) => {
    const raw = swatch.getAttribute('data-sibling-images') || '';
    if (raw) {
      return raw.split('||').filter(Boolean).map((part) => {
        const sep = part.indexOf('::');
        if (sep === -1) return { src: part, srcset: '' };
        return { src: part.slice(0, sep), srcset: part.slice(sep + 2) };
      });
    }
    const src = swatch.getAttribute('data-sibling-image') || '';
    const srcset = swatch.getAttribute('data-sibling-srcset') || '';
    return src ? [{ src, srcset }] : [];
  };

  const preloadSiblingImages = (swatch) => {
    parseSiblingImages(swatch).forEach(({ src }) => {
      if (!src || preloadedSiblingImages.has(src)) return;
      preloadedSiblingImages.add(src);
      const img = new Image();
      img.src = src;
    });
  };

  /* ---------- Product card icon tone (light photo → black icons, dark → white) ---------- */
  const iconToneCanvas = document.createElement('canvas');
  const iconToneCtx = iconToneCanvas.getContext('2d', { willReadFrequently: true });
  const ICON_TONE_LUMINANCE = 138;
  const iconTonePending = new WeakSet();
  const iconToneProbeCache = new Map();

  const getActiveMediaSlideIndex = (track) => {
    const slides = track.querySelectorAll('.product-card__media-slide');
    if (!slides.length) return 0;
    const w = track.clientWidth;
    if (!w) return 0;
    const scroll = Math.abs(track.scrollLeft);
    return Math.max(0, Math.min(slides.length - 1, Math.round(scroll / w)));
  };

  const initCartUpsellDrawer = (root = document) => {
    const drawer = root?.matches?.('[data-cart-drawer]')
      ? root
      : root.querySelector?.('[data-cart-drawer]') || document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    drawer.querySelectorAll('[data-cart-upsell-card] [data-product-media-track]').forEach((track) => {
      initProductMediaTrack(track);
      const card = track.closest('[data-cart-upsell-card]');
      const dotsWrap = card?.querySelector('[data-cart-upsell-dots]');
      if (!dotsWrap || track._upsellDotsBound) return;
      track._upsellDotsBound = true;
      const dots = dotsWrap.querySelectorAll('.cart-upsell-card__dot');
      const updateDots = () => {
        const idx = getActiveMediaSlideIndex(track);
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === idx));
      };
      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(track._upsellDotTimer);
          track._upsellDotTimer = setTimeout(updateDots, 50);
        },
        { passive: true }
      );
      updateDots();
    });
  };

  const getCardMediaImage = (card) => {
    const track = card.querySelector('[data-product-media-track]');
    if (track?.clientWidth > 0) {
      const slides = track.querySelectorAll('.product-card__media-slide');
      const tracked = slides[getActiveMediaSlideIndex(track)]?.querySelector('.product-card__media-primary');
      if (tracked) return tracked;
    }
    return card.querySelector('.product-card__media-primary');
  };

  const luminanceFromPixel = (data, i) => {
    if (data[i + 3] < 40) return null;
    return 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
  };

  const avgBandLuminance = (data, width, height, xStart, xEnd) => {
    let sum = 0;
    let count = 0;
    const x0 = Math.max(0, xStart);
    const x1 = Math.min(width, xEnd);
    for (let y = 0; y < height; y += 1) {
      for (let x = x0; x < x1; x += 2) {
        const lum = luminanceFromPixel(data, (y * width + x) * 4);
        if (lum == null) continue;
        sum += lum;
        count += 1;
      }
    }
    return count ? sum / count : null;
  };

  const luminanceToTone = (lum) => (lum == null || lum >= ICON_TONE_LUMINANCE ? 'light' : 'dark');

  /* Bottom band at inline-start + inline-end (cart + wishlist); min luminance → both icons same color */
  const measureIconRegionLuminance = (img) => {
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return null;
    const bandH = Math.min(Math.max(18, Math.floor(nh * 0.24)), nh);
    const srcY = nh - bandH;
    const sampleW = Math.min(96, nw);
    const sampleH = Math.min(36, bandH);
    iconToneCanvas.width = sampleW;
    iconToneCanvas.height = sampleH;
    try {
      iconToneCtx.drawImage(img, 0, srcY, nw, bandH, 0, 0, sampleW, sampleH);
      const { data } = iconToneCtx.getImageData(0, 0, sampleW, sampleH);
      const bandEdge = Math.ceil(sampleW * 0.36);
      const bandInner = Math.floor(sampleW * 0.64);
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      /* LTR: cart=start=left, wishlist=end=right. RTL: cart=start=right, wishlist=end=left */
      const startLum = rtl
        ? avgBandLuminance(data, sampleW, sampleH, bandInner, sampleW)
        : avgBandLuminance(data, sampleW, sampleH, 0, bandEdge);
      const endLum = rtl
        ? avgBandLuminance(data, sampleW, sampleH, 0, bandEdge)
        : avgBandLuminance(data, sampleW, sampleH, bandInner, sampleW);
      if (startLum == null && endLum == null) return null;
      if (startLum == null) return endLum;
      if (endLum == null) return startLum;
      return Math.min(startLum, endLum);
    } catch (_) {
      return null;
    }
  };

  const probeImageLuminance = (src) => {
    if (!src) return Promise.resolve(null);
    const dir = document.documentElement.getAttribute('dir') || 'ltr';
    const cacheKey = `region|${src}|${dir}`;
    if (iconToneProbeCache.has(cacheKey)) return iconToneProbeCache.get(cacheKey);
    const pending = new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => resolve(measureIconRegionLuminance(probe));
      probe.onerror = () => resolve(null);
      probe.src = src;
    });
    iconToneProbeCache.set(cacheKey, pending);
    return pending;
  };

  const resolveIconLuminance = async (img) => {
    try {
      const direct = measureIconRegionLuminance(img);
      if (direct != null) return direct;
    } catch (_) {}
    const src = img.currentSrc || img.src;
    if (!src) return null;
    return probeImageLuminance(src);
  };

  const applyCardIconTone = (card, tone) => {
    if (!card?.querySelector('.product-card__icons')) return;
    card.dataset.mediaTone = tone === 'dark' ? 'dark' : 'light';
    card.querySelectorAll('.product-card__icon-btn').forEach((btn) => {
      delete btn.dataset.mediaTone;
    });
  };

  const updateCardIconTone = (card) => {
    if (!card?.querySelector('.product-card__icons')) return;
    const img = getCardMediaImage(card);
    if (!img) {
      applyCardIconTone(card, 'light');
      return;
    }
    const commit = async () => {
      const lum = await resolveIconLuminance(img);
      applyCardIconTone(card, luminanceToTone(lum));
    };
    if (img.complete && img.naturalWidth) {
      commit();
      return;
    }
    img.addEventListener('load', () => commit(), { once: true });
    img.addEventListener('error', () => applyCardIconTone(card, 'light'), { once: true });
  };

  const scheduleCardIconTone = (card) => {
    if (!card || iconTonePending.has(card)) return;
    iconTonePending.add(card);
    requestAnimationFrame(() => {
      iconTonePending.delete(card);
      updateCardIconTone(card);
    });
  };

  const bindCardIconTone = (card) => {
    if (!card?.querySelector('.product-card__icons') || card._iconToneBound) return;
    card._iconToneBound = true;
    const track = card.querySelector('[data-product-media-track]');
    if (track) {
      let scrollTimer;
      track.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => scheduleCardIconTone(card), 80);
      }, { passive: true });
    }
    card.querySelectorAll('.product-card__media-primary').forEach((img) => {
      img.addEventListener('load', () => scheduleCardIconTone(card), { passive: true });
    });
  };

  const refreshAllCardIconTones = () => {
    iconToneProbeCache.clear();
    $$('[data-product-card]').forEach((card) => {
      card._iconToneBound = false;
      bindCardIconTone(card);
      scheduleCardIconTone(card);
    });
  };

  const initProductCardIconTones = (root = document) => {
    $$('[data-product-card]', root).forEach((card) => {
      bindCardIconTone(card);
      scheduleCardIconTone(card);
    });
  };

  if (document.documentElement) {
    new MutationObserver(() => refreshAllCardIconTones()).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['dir', 'lang'],
    });
  }

  const getCardImagesFromDom = (card) => Array.from(card.querySelectorAll('.product-card__media-primary'))
    .map((img) => ({ src: img.currentSrc || img.src, srcset: img.srcset || '' }))
    .filter((item) => item.src);

  const cacheCardMedia = (card) => {
    const images = getCardImagesFromDom(card);
    if (images.length) card.dataset.cardMediaCache = JSON.stringify(images);
  };

  const getCardMediaCache = (card) => {
    try {
      return JSON.parse(card.dataset.cardMediaCache || '[]');
    } catch (_) {
      return [];
    }
  };

  const setCardMedia = (card, images, title) => {
    const mediaWrap = card.querySelector('.product-card__media');
    if (!mediaWrap) return;

    let valid = images.filter((item) => item?.src && item.src.trim());
    if (!valid.length) {
      valid = getCardMediaCache(card);
      if (!valid.length) return;
    }

    const previousMarkup = mediaWrap.innerHTML;
    const cached = getCardMediaCache(card);
    const restoreOnError = () => {
      if (cached.length) {
        setCardMedia(card, cached, title);
        return;
      }
      if (previousMarkup) mediaWrap.innerHTML = previousMarkup;
    };

    let track = mediaWrap.querySelector('[data-product-media-track]');
    if (track) releaseMediaTrack(track);

    const buildImg = ({ src, srcset }, index) => {
      const img = document.createElement('img');
      img.className = 'product-card__media-primary';
      img.src = src;
      if (srcset) img.srcset = srcset;
      img.sizes = '(min-width: 769px) 329px, (min-width: 391px) 224px, 177px';
      img.width = 329;
      img.height = 362;
      img.loading = index === 0 ? 'eager' : 'lazy';
      img.decoding = index === 0 ? 'sync' : 'async';
      if (index === 0) img.fetchPriority = 'high';
      if (title) img.alt = title;
      img.addEventListener('error', restoreOnError, { once: true });
      return img;
    };

    mediaWrap.innerHTML = '';

    if (valid.length === 1) {
      mediaWrap.classList.remove('product-card__media--swipeable');
      mediaWrap.appendChild(buildImg(valid[0], 0));
    } else {
      track = document.createElement('div');
      track.className = 'product-card__media-track';
      track.dataset.productMediaTrack = '';
      const frag = document.createDocumentFragment();
      valid.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = 'product-card__media-slide';
        slide.appendChild(buildImg(item, index));
        frag.appendChild(slide);
      });
      track.appendChild(frag);
      mediaWrap.appendChild(track);
      track.scrollLeft = 0;
      mediaWrap.classList.add('product-card__media--swipeable');
      initProductMediaTrack(track);
    }

    card.dataset.cardMediaCache = JSON.stringify(valid);
    card._iconToneBound = false;
    bindCardIconTone(card);
    scheduleCardIconTone(card);
  };

  $$('[data-product-media-track]').forEach(initProductMediaTrack);
  initCartUpsellDrawer();
  initProductCardIconTones();

  /* ---------- Product cards — cache media + decorative swatches ---------- */
  $$('[data-product-card]').forEach((card) => {
    cacheCardMedia(card);

    const decorativeSwatches = card.querySelectorAll('.product-card__swatch:not([data-sibling-swatch])');
    if (!decorativeSwatches.length) return;

    decorativeSwatches.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        decorativeSwatches.forEach((el) => {
          const active = el === swatch;
          el.setAttribute('aria-pressed', active ? 'true' : 'false');
          if (active) el.setAttribute('aria-current', 'true');
          else el.removeAttribute('aria-current');
        });
      });
    });
  });

  /* ---------- Product sibling swatches (gold / silver SKU swap) ---------- */
  $$('[data-product-card]').forEach((card) => {
    const swatches = card.querySelectorAll('[data-sibling-swatch]');
    if (!swatches.length) return;

    cacheCardMedia(card);

    const mediaLink = card.querySelector('.product-card__media-link');
    const titleLink = card.querySelector('[data-product-title]');
    const materialEl = card.querySelector('[data-product-material]');
    const priceEl = card.querySelector('[data-product-price]');
    const quickAddBtns = card.querySelectorAll('[data-quick-add]');
    const wishlistBtns = card.querySelectorAll('[data-wishlist-toggle]');

    const applySibling = (swatch) => {
      const siblingUrl = swatch.getAttribute('data-sibling-url') || '';
      const siblingTitle = swatch.getAttribute('data-sibling-title') || '';
      const siblingMaterial = swatch.getAttribute('data-sibling-material') || '';
      const siblingPrice = swatch.getAttribute('data-sibling-price') || '';
      const siblingVariantId = swatch.getAttribute('data-sibling-variant-id') || '';
      const siblingId = swatch.getAttribute('data-sibling-id') || '';

      if (titleLink && siblingTitle) {
        if (siblingUrl) {
          titleLink.href = siblingUrl;
          titleLink.dataset.productUrl = siblingUrl;
        }
        titleLink.textContent = siblingTitle;
      }
      if (materialEl) {
        materialEl.textContent = siblingMaterial || '';
        materialEl.hidden = !siblingMaterial;
      }
      if (priceEl && siblingPrice) {
        const priceText = siblingPrice.replace(/<[^>]*>/g, '').trim();
        if (priceText && !/^0(\.0+)?\s/.test(priceText) && priceText !== '0.00') {
          priceEl.innerHTML = siblingPrice;
        }
      }
      if (mediaLink && siblingUrl) {
        mediaLink.href = siblingUrl;
        mediaLink.dataset.productUrl = siblingUrl;
        if (siblingTitle) mediaLink.setAttribute('aria-label', siblingTitle);
      }

      const siblingImages = parseSiblingImages(swatch).filter((item) => item?.src && item.src.trim());
      const siblingImageOnly = (swatch.getAttribute('data-sibling-image') || '').trim();
      if (siblingImages.length) {
        setCardMedia(card, siblingImages, siblingTitle);
      } else if (siblingImageOnly) {
        setCardMedia(card, [{
          src: siblingImageOnly,
          srcset: swatch.getAttribute('data-sibling-srcset') || '',
        }], siblingTitle);
      }

      quickAddBtns.forEach((btn) => {
        if (siblingVariantId) btn.dataset.quickAdd = siblingVariantId;
      });
      wishlistBtns.forEach((btn) => {
        if (siblingId) btn.dataset.wishlistToggle = siblingId;
        if (siblingVariantId) btn.dataset.variantId = siblingVariantId;
        if (siblingUrl) btn.dataset.productUrl = siblingUrl;
        setWishlistBtnState(btn, readLocalWishlist().some((item) => wishlistItemKey(item.productId, item.variantId) === wishlistItemKey(siblingId, siblingVariantId)));
      });
      if (siblingId) card.dataset.productId = siblingId;

      swatches.forEach((el) => {
        const active = el === swatch;
        el.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
    };

    swatches.forEach((swatch) => {
      swatch.addEventListener('pointerenter', () => preloadSiblingImages(swatch), { passive: true });
      swatch.addEventListener('focus', () => preloadSiblingImages(swatch));
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        releaseMediaTrack(card.querySelector('[data-product-media-track]'));
        applySibling(swatch);
      });
    });
  });

  /* ---------- Featured row drag scroll (desktop mouse swipe) ---------- */
  $$('[data-scroll-track]').forEach((row) => {
    let dragging = false;
    let moved = false;
    let startX = 0;
    let scrollStart = 0;

    const isInteractiveTarget = (target) => target.closest(
      'button, [data-quick-add], [data-wishlist-toggle], [data-sibling-swatch], .product-card__swatch, a[href], .irl-card__dock-link, [data-irl-video-trigger], [data-irl-dock-expand]'
    );

    row.addEventListener('pointerdown', (e) => {
      if (isInteractiveTarget(e.target)) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      scrollStart = row.scrollLeft;
      row.setPointerCapture(e.pointerId);
    });
    row.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      row.scrollLeft = scrollStart - dx;
    });
    const end = () => { dragging = false; };
    row.addEventListener('pointerup', end);
    row.addEventListener('pointercancel', end);
    row.addEventListener('click', (e) => {
      if (!moved) return;
      e.preventDefault();
      e.stopPropagation();
      moved = false;
    }, true);
  });

  /* ---------- IRL — scroll card to center on video tap ---------- */
  const scrollIrlCardToCenter = (track, card) => {
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const target = track.scrollLeft + (cardRect.left - trackRect.left) - (trackRect.width - cardRect.width) / 2;
    track.scrollTo({ left: target, behavior: 'smooth' });
  };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-irl-video-trigger]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    const card = trigger.closest('.irl-card, .pdp-irl-card');
    const track = trigger.closest('[data-irl-track]');
    if (!card || !track) return;
    scrollIrlCardToCenter(track, card);
  });

  document.addEventListener('click', (e) => {
    const muteBtn = e.target.closest('[data-pdp-irl-mute], [data-pdp-irl-modal-mute]');
    if (!muteBtn) return;
    e.preventDefault();
    e.stopPropagation();
    const media = muteBtn.closest('.pdp-irl-card__media, .pdp-irl-modal__video-wrap');
    const video = media?.querySelector('.pdp-irl-card__video, .pdp-irl-modal__video, .irl-card__video');
    if (!video || video.tagName !== 'VIDEO') return;
    video.muted = !video.muted;
    muteBtn.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
    if (!video.muted) video.play().catch(() => {});
  });

  const closeIrlDocks = (except) => {
    $$('.irl-card__dock.is-expanded').forEach((dock) => {
      if (dock === except) return;
      dock.classList.remove('is-expanded');
      const btn = dock.querySelector('[data-irl-dock-expand]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (e) => {
    const expandBtn = e.target.closest('[data-irl-dock-expand]');
    if (expandBtn) {
      e.preventDefault();
      e.stopPropagation();
      const dock = expandBtn.closest('.irl-card__dock');
      if (!dock) return;
      const isExpanded = dock.classList.contains('is-expanded');
      closeIrlDocks();
      if (!isExpanded) {
        dock.classList.add('is-expanded');
        expandBtn.setAttribute('aria-expanded', 'true');
      }
      return;
    }
    closeIrlDocks();
  });

  /* Keep homepage IRL videos muted autoplay (PDP videos can be unmuted via mute button) */
  $$('.irl-card .irl-card__video').forEach((video) => {
    video.muted = true;
    video.setAttribute('muted', '');
    const play = () => video.play().catch(() => {});
    play();
    video.addEventListener('volumechange', () => {
      if (!video.muted) {
        video.muted = true;
      }
    });
  });

  $$('.pdp-irl-card__video').forEach((video) => {
    if (video.tagName !== 'VIDEO') return;
    video.muted = true;
    video.setAttribute('muted', '');
    video.play().catch(() => {});
  });

  /* ---------- Announcement bar promos ---------- */

  if (primaryBar) {
    const closeBtn = primaryBar.querySelector('[data-announcement-close]');
    closeBtn?.addEventListener('click', () => {
      primaryBar.classList.add('is-hidden');
      updateStickyNav();
    });
  }

  document.querySelectorAll('[data-announcement-promos]').forEach((wrap) => {
    const promos = [...wrap.querySelectorAll('[data-announcement-promo]')];
    if (promos.length < 2) return;
    const section = wrap.closest('[data-announcement-stack]');
    const rotateSec = Number(section?.dataset.rotateSeconds) || 5;
    let index = 0;
    setInterval(() => {
      promos[index]?.classList.remove('is-active');
      index = (index + 1) % promos.length;
      promos[index]?.classList.add('is-active');
    }, Math.max(3, rotateSec) * 1000);
  });

  updateStickyNav();
  window.addEventListener('resize', updateStickyNav, { passive: true });

  /* ---------- Mobile locale (popover or sheet) ---------- */
  const localeSheet = document.querySelector('[data-locale-sheet]');
  const localePopover = document.querySelector('[data-locale-popover]');
  const localePanel = localePopover?.querySelector('[data-locale-panel]');
  const localeTriggers = $$('[data-locale-toggle]');

  const setLocaleOpen = (open) => {
    localeTriggers.forEach((btn) => btn.setAttribute('aria-expanded', open ? 'true' : 'false'));
    if (localePopover) {
      localePopover.dataset.open = open ? 'true' : 'false';
      localePanel?.setAttribute('aria-hidden', open ? 'false' : 'true');
      return;
    }
    if (!localeSheet) return;
    localeSheet.dataset.open = open ? 'true' : 'false';
    localeSheet.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (overlay) overlay.dataset.open = open ? 'true' : 'false';
    document.documentElement.classList.toggle('locale-sheet-open', open);
    document.documentElement.style.overflow = open ? 'hidden' : '';
    document.body.style.overflow = open ? 'hidden' : '';
  };

  localeTriggers.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const root = localePopover || localeSheet;
      if (!root) return;
      const open = root.dataset.open !== 'true';
      setLocaleOpen(open);
    });
  });

  localeSheet?.querySelector('[data-locale-close]')?.addEventListener('click', () => setLocaleOpen(false));
  localeSheet?.addEventListener('click', (e) => {
    if (e.target === localeSheet) setLocaleOpen(false);
  });
  document.addEventListener('click', (e) => {
    if (localePopover?.dataset.open === 'true' && !e.target.closest('[data-locale-popover]')) {
      setLocaleOpen(false);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (localePopover?.dataset.open === 'true' || localeSheet?.dataset.open === 'true') setLocaleOpen(false);
  });

  /* ---------- Testimonials carousel (mobile / tablet) ---------- */
  const testimonialsMq = window.matchMedia('(max-width: 1023px)');
  $$('[data-testimonials-carousel]').forEach((track) => {
    const board = track.closest('.testimonials__board');
    if (!board) return;
    const prev = board.querySelector('[data-testimonials-prev]');
    const next = board.querySelector('[data-testimonials-next]');
    const rtl = document.documentElement.getAttribute('dir') === 'rtl';

    const scrollBySlide = (dir) => {
      const step = track.clientWidth || 1;
      const delta = dir * step * (rtl ? -1 : 1);
      track.scrollBy({ left: delta, behavior: 'smooth' });
    };

    prev?.addEventListener('click', () => scrollBySlide(-1));
    next?.addEventListener('click', () => scrollBySlide(1));
    track.addEventListener('keydown', (e) => {
      if (!testimonialsMq.matches) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollBySlide(rtl ? 1 : -1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollBySlide(rtl ? -1 : 1);
      }
    });
  });

  hydrateCartWrapAddForm();
  bindPreviewThemePersistence();

  const initHeroVideos = () => {
    const mq = window.matchMedia('(max-width: 1024px)');

    const playHeroVideo = (wrap, video, rate) => {
      if (!video || document.hidden) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.playbackRate = rate;
      video.defaultPlaybackRate = rate;
      const attempt = () => {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      };
      if (video.readyState >= 2) attempt();
      else video.addEventListener('loadeddata', attempt, { once: true });
      video.addEventListener(
        'playing',
        () => {
          wrap.classList.add('is-video-ready');
        },
        { once: true }
      );
    };

    const idleHeroVideo = (video) => {
      if (!video) return;
      video.pause();
      video.preload = 'none';
    };

    const activateHeroVideo = (wrap, video, rate) => {
      if (!video) return;
      if (video.preload === 'none') {
        video.preload = 'auto';
        video.load();
      }
      playHeroVideo(wrap, video, rate);
    };

    const syncHeroVideo = (wrap) => {
      const rateRaw = parseFloat(wrap.dataset.playbackRate || '1.2');
      const rate = Number.isFinite(rateRaw) ? Math.min(1.5, Math.max(1, rateRaw)) : 1.2;
      const desktop = wrap.querySelector('.hero__video--desktop');
      const mobile = wrap.querySelector('.hero__video--mobile');
      const useMobile = mq.matches;
      idleHeroVideo(useMobile ? desktop : mobile);
      activateHeroVideo(wrap, useMobile ? mobile : desktop, rate);
    };

    $$('[data-hero-video]').forEach((wrap) => {
      syncHeroVideo(wrap);
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', () => syncHeroVideo(wrap));
      } else {
        mq.addListener(() => syncHeroVideo(wrap));
      }
    });

    document.addEventListener('visibilitychange', () => {
      $$('[data-hero-video]').forEach((wrap) => {
        const useMobile = mq.matches;
        const active = wrap.querySelector(useMobile ? '.hero__video--mobile' : '.hero__video--desktop');
        if (document.hidden) active?.pause();
        else active?.play()?.catch(() => {});
      });
    });
  };

  initHeroVideos();
})();
